import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Button, Typography, MenuItem, TextField, Autocomplete, Tooltip, IconButton } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
  initialPharmacyValues,
  validationPharmacySchema,
  useBillingConfigOptions,
  useExistingPatientPrescriptions,
  useExistingPatientSelectedPrescriptions,
  PatientSourceWithTariffs,
  PharmacyFormValues,
  Option,
  PharmacyItem as FormPharmacyItem,
  PrescriptionItem,
  useCreatePharmacyInvoice,
  buildCreatePharmacyInvoiceParams,
  mapFormToPharmacyInvoicePayload,
  PackageOption,
  Package,
  OptionforConssession,
  TariffPackageInfo,
  useUpdateInvoice,
  buildUpdatePharmacyInvoiceParams,
  calculateFromInclusiveMRP,
  PackageDetail,
  hasAnyQtyError,
} from './newPharmacyForm.helper';
import { useActivePackages, type PackageDetails } from './packageSelect.helper';
import PatientSourceSelect from './PatientSourceSelect';
import TariffSelect from './TariffSelect';
import PackageSelect from './PackageSelect';
import ItemSearchField, { PharmacyItem as ApiPharmacyItem, PharmacyItemBatch } from './ItemSearchField';
import BatchSelectionModal from './BatchSelectionModal';
import ItemsList from './ItemsList';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { usePatientContext } from '../../../../components/patinetProvider';
import { Formik } from 'formik';
import { useClinic } from '../../../../components/ClinicProvider';
import PreviousPrescribed from './PreviousPrescribed';
import SummaryCard from '../../Invoice/newInvoice/SummaryCard';
import useCurrency from '../../../../components/CurrencyProvider';
import { CollectPaymentModal, PaymentDetails, PaymentFormValues, PaymentHistoryItem, buildPaymentApiPayload } from '../../collectPayment';
import {
  useCreatePayment,
  buildCreatePaymentParams,
  useValidatePackageAdvance,
  buildValidatePackageAdvanceParams,
  fetchLinkedRecipients,
  markSettlementSource,
  assignToRecipient,
} from '../../Invoice/newInvoice/newInvoiceForm.helper';
import { Department, mapUserClinicsResponse, UserClinicDepartments } from '../../../Inventory/Common/inventory.helper';
import { WalletAdvance } from '../../collectPayment';
import { formatDateYMD, formatDateDMY, getTodayYMD, toISODateString, getDateFromToday } from '../../../../utils/dateUtils';
import { RequiredLabel } from '../../../../utils/commonUtils';
import { outlinedGrayButtonSx } from '../../../../utils/typographyStyles';
import CollectPackageAdvanceModal, { PackageOption as AdvancePackageOption, PackageAdvanceFormData } from '../../Invoice/newInvoice/CollectPackageAdvanceModal';
import InsufficientAdvancePopup from '../../Invoice/newInvoice/InsufficientAdvancePopup';
import RecipientSelectionModal, { Recipient } from '../../Invoice/newInvoice/RecipientSelectionModal';
import PharmacyPackageItemsModal, { PharmacyPackageConsumptionItem } from './PharmacyPackageItemsModal';
import { useAlert } from '../../../../components/alertProvider';
import { mapSelectedPrescriptionToFormItem, mapPatientDrugToFormItem } from './byPharmacyItemMapper';
import { ConfirmationModal } from '../../../../components/confirmationModal';
import { PatientDrug } from '../../Invoice/invoiceHelper';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postRequest } from '../../../../services/httpService';
import PlaylistAddRoundedIcon from '@mui/icons-material/PlaylistAddRounded';
import { postMakeAdvancePaymentURL, MakePayment, PaymentDetailPayload } from '../../Advance/advance.helper';

/**
 * Effectful helper rendered inside Formik to auto-clear the concession reason
 * when no item has a concession.
 */
const ConcessionReasonAutoClear: React.FC<{
  hasConcession: boolean;
  concessionReason: string;
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
}> = ({ hasConcession, concessionReason, setFieldValue }) => {
  useEffect(() => {
    if (!hasConcession && concessionReason) {
      setFieldValue('concessionReason', '', false);
    }
  }, [hasConcession, concessionReason, setFieldValue]);
  return null;
};

const NewPharmacyForm = () => {
  /**
   * -----------------------------------------------------------------------
   * Local component state
   * -----------------------------------------------------------------------
   */
  const [patientSourceOptions, setPatientSourceOptions] = useState<PatientSourceWithTariffs[]>([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [prescriptionsLoaded, setPrescriptionsLoaded] = useState(false);
  const [concessionReasonOptions, setConcessionReasonOptions] = useState<OptionforConssession[]>([]);
  const [packageOptions, setPackageOptions] = useState<PackageOption[]>([]);
  // Selected package for applying package rules (e.g., concession limits, rate editing)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedPackage, setSelectedPackage] = useState<Package | undefined>(undefined);

  // Collect Payment Modal state
  const [collectPaymentOpen, setCollectPaymentOpen] = useState(false);
  const [paymentDetailsForModal, setPaymentDetailsForModal] = useState<PaymentDetails | null>(null);
  const [paymentModes, setPaymentModes] = useState<{ id: number; name: string; code: string }[]>([]);
  const [walletAdvances, setWalletAdvances] = useState<WalletAdvance[]>([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [initialPaymentHistory, setInitialPaymentHistory] = useState<PaymentHistoryItem[]>([]);
  const [consumeFromPackageForModal, setConsumeFromPackageForModal] = useState<number>(0);
  
  /**
   * -----------------------------------------------------------------------
   * Package Advance Collection State (3-Step Modal Flow)
   * -----------------------------------------------------------------------
   */
  // Step 1 state: Package advance form modal
  const [collectPackageAdvanceOpen, setCollectPackageAdvanceOpen] = useState(false);
  const [packageOptionsForAdvance, setPackageOptionsForAdvance] = useState<AdvancePackageOption[]>([]);
  
  // Step 2 state: Payment methods modal (after advance form)
  const [collectPackagePaymentOpen, setCollectPackagePaymentOpen] = useState(false);
  const [packageAdvancePaymentDetails, setPackageAdvancePaymentDetails] = useState<PaymentDetails | null>(null);
  const [packageAdvanceFormData, setPackageAdvanceFormData] = useState<PackageAdvanceFormData | null>(null);
  
  // Insufficient advance popup state
  const [insufficientAdvanceOpen, setInsufficientAdvanceOpen] = useState(false);
  
  // Store validation result packages for auto-populating advance modal
  const [validationResultPackages, setValidationResultPackages] = useState<
    { package_id: number; required_amount: number; balance_amount: number }[]
  >([]);
  
  // Ref to track modal transition timers for cleanup on unmount
  const modalTransitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Package full pay (all from package) state
  const [packageFullPayConfirmOpen, setPackageFullPayConfirmOpen] = useState(false);
  const [formValuesForPayment, setFormValuesForPayment] = useState<PharmacyFormValues | null>(null);

  // Add Package Item modal state
  const [packageItemModalOpen, setPackageItemModalOpen] = useState(false);
  const [selectedConsumptionPkg, setSelectedConsumptionPkg] = useState<PackageDetails | null>(null);

  // Donor/Surrogate billing state
  const DONOR_SURROGATE_TYPES = ['SPERM_DONOR', 'OOCYTE_DONOR', 'SURROGATE'];
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [selectedRecipientId, setSelectedRecipientId] = useState<number | null>(null);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<PharmacyFormValues | null>(null);
  
  // Use useRef instead of useState to avoid re-renders
  const formikRef = useRef<{
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
    setFieldTouched: (field: string, touched?: boolean, shouldValidate?: boolean) => void;
    values: PharmacyFormValues;
      } | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { id: patientIdParam } = useParams<{ id: string }>();
  const patientId = patientIdParam ?? undefined;
  
  // Get appointment_id from route state (passed from table row click)
  const appointmentId = (location.state as { appointment_id?: number })?.appointment_id;
  
  const invoiceId = (location.state as { invoice_id?: number })?.invoice_id;

  // Get selected drugs from "By Pharmacy" tab navigation
  const selectedDrugs = (location.state as { selectedDrugs?: PatientDrug[] })?.selectedDrugs;

  // New/temp patient flag — hides Patient Source, Tariff, Package Selection
  const isNewPatient = (location.state as { isNewPatient?: boolean })?.isNewPatient ?? false;

  // Get patient data from context
  const { MainpatientDetails } = usePatientContext();
  const currentPatient = MainpatientDetails;
  const patientFullName = currentPatient 
    ? `${currentPatient.first_name ?? ''} ${currentPatient.last_name ?? ''}`.trim() 
    : '';
  const patientMRN = currentPatient?.medical_record_no ?? '';
  const isDonorOrSurrogate = DONOR_SURROGATE_TYPES.includes(currentPatient?.patient_type ?? '');

  const { ClinicId } = useClinic();
  const { getInvoiceDueDate, currency } = useCurrency();
  const { addAlert } = useAlert();
  const queryClient = useQueryClient();
  
  // Calculate min (today) and max (today + invoice_due_days) for due date picker
  const minDueDate = getTodayYMD();
  const maxDueDate = formatDateYMD(getInvoiceDueDate(new Date()));
  // Calculate 30 days from today for dispensed items
  const dispensedDueDate = getDateFromToday(30);
  const [storeList, setStoreList] = useState<Department[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
    
  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user') || '{}';
      const user = JSON.parse(userStr) as UserClinicDepartments;
      const mapped = mapUserClinicsResponse(user);
      const defaultClinic = mapped.find((c) => c.id === Number(ClinicId));
      if (defaultClinic) {
        const departments = defaultClinic.departments || [];
        setStoreList(departments);
        // Set default selected store to department with is_default_department true
        const defaultDept = departments.find((d) => d.is_default_department === true);
        if (defaultDept) {
          setSelectedStoreId(defaultDept.id);
        } else {
          // Fallback: select "Pharmacy" by name if no default department is set
          const pharmacyDept = departments.find(
            (d) => d.name_label?.toLowerCase() === 'pharmacy' || d.name?.toLowerCase() === 'pharmacy'
          );
          if (pharmacyDept) {
            setSelectedStoreId(pharmacyDept.id);
          } else if (departments.length > 0) {
            // Fallback: select first department if "Pharmacy" not found
            setSelectedStoreId(departments[0].id);
          }
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error parsing user from localStorage:', err);
      setStoreList([]);
    }
  }, [ClinicId]);

  /**
   * -----------------------------------------------------------------------
   * API calls (TanStack Query) 
   * -----------------------------------------------------------------------
   */

  // Billing configuration options (patient source & tariff)
  const {
    data: billingConfigData,
    isLoading: isConfigLoading,
    isError: isConfigError,
    error: configError,
  } = useBillingConfigOptions(patientId);


  // eslint-disable-next-line no-console
  console.log('appointmentId-----------', appointmentId);

  // eslint-disable-next-line no-console
  console.log('invoiceId-----------', invoiceId);
  
  // Existing patient prescriptions
  const {
    data: prescriptionsData,
    isLoading: isPrescriptionsLoading,
  } = useExistingPatientPrescriptions(appointmentId, invoiceId);

  // Fetch batch details for items selected from "By Pharmacy" tab
  const selectedDrugIds = React.useMemo(
    () => selectedDrugs?.map(d => d.id),
    [selectedDrugs]
  );
  const {
    data: selectedPrescriptionsData,
    isLoading: isSelectedPrescriptionsLoading,
  } = useExistingPatientSelectedPrescriptions(selectedDrugIds, selectedStoreId);

  // Packages - use active packages for the patient (not all packages)
  const {
    data: activePackagesData,
    isLoading: isPackagesLoading,
    error: activePackagesError,
    refetch: refetchActivePackages,
  } = useActivePackages(
    isDonorOrSurrogate ? undefined : patientId,
    { enabled: !!patientId, donorId: isDonorOrSurrogate ? patientId ?? undefined : undefined }
  );

  // Create pharmacy invoice mutation
  const createPharmacyInvoiceMutation = useCreatePharmacyInvoice();
  const updateInvoice = useUpdateInvoice();
  
  // Create payment mutation
  const createPaymentMutation = useCreatePayment();
  
  // Validate package advance mutation
  const validatePackageAdvanceMutation = useValidatePackageAdvance();

  /**
   * Mutation for submitting package advance payment
   * Uses the same API endpoint as Advance_Request module
   */
  const { mutate: submitAdvancePayment, isPending: isAdvanceSubmitting } = useMutation({
    mutationFn: (payload: MakePayment) => postRequest(postMakeAdvancePaymentURL(), payload),
    onSuccess: async () => {
      addAlert('Package advance collected successfully', 'success');
      setCollectPackagePaymentOpen(false);
      setPackageAdvanceFormData(null);
      setPackageAdvancePaymentDetails(null);
      // Invalidate active packages to refresh package advance data
      // Uses partial match - any query starting with 'active-packages' for this patient
      await queryClient.invalidateQueries({ 
        queryKey: ['active-packages', { patient_id: patientId, pharmacy: true }],
      });
    },
    onError: (err: unknown) => {
      const message = (err as { message?: string })?.message || 'Failed to save advance payment.';
      addAlert(message, 'error');
    },
  });

  // Cleanup modal transition timer on unmount
  useEffect(() => {
    return () => {
      if (modalTransitionTimerRef.current) {
        clearTimeout(modalTransitionTimerRef.current);
      }
    };
  }, []);

  /**
   * Handle "Continue with Recipient" → Proceed flow for pharmacy.
   * Creates the pharmacy invoice, then calls mark-settlement-source API.
   * Does NOT open the payment popup.
   */
  const handleRecipientProceed = async (recipientId?: number) => {
    if (!pendingFormValues) return;
    const values = pendingFormValues;
    const treatmentDoctorId = prescriptionsData?.data?.patient_info?.treating_doctor_id || 0;
    const visitDate = prescriptionsData?.data?.patient_info?.visit_date || getTodayYMD();
    const selectedSource = patientSourceOptions.find(src => src.value === values.patientSource);
    const selectedTariff = selectedSource?.tariffs?.find(t => t.tariff_code === values.tariff);
    const selectedPkg = packageOptions.find(pkg => pkg.value === values.packageId);
    const tariffPackageInfo: TariffPackageInfo = {
      tariffId: selectedTariff?.tariff_id ?? null,
      tariffName: selectedTariff?.tariff_name,
      packageId: selectedPkg?.package?.id ?? null,
      packageName: selectedPkg?.label,
    };

    const invoicePayload = mapFormToPharmacyInvoicePayload(
      values,
      Number(patientId),
      Number(appointmentId),
      treatmentDoctorId,
      visitDate,
      tariffPackageInfo,
      'DRAFT',
      false,
      selectedStoreId
    );
    // Override to mark as donor invoice
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (invoicePayload as any).invoice_patient_type = 'donor';

    try {
      let finalInvoiceId: number | undefined;

      if (invoiceId) {
        await updateInvoice.mutateAsync(
          buildUpdatePharmacyInvoiceParams(invoiceId, invoicePayload)
        );
        finalInvoiceId = Number(invoiceId);
      } else {
        const createdInvoice = await createPharmacyInvoiceMutation.mutateAsync(
          buildCreatePharmacyInvoiceParams(invoicePayload)
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const resp = createdInvoice as any;
        finalInvoiceId = resp?.id ?? resp?.invoice_id;
      }

      if (finalInvoiceId) {
        await markSettlementSource(finalInvoiceId, 'recipient');
        if (recipientId) {
          await assignToRecipient(finalInvoiceId, recipientId);
          addAlert('Pharmacy invoice assigned to recipient successfully', 'success');
        } else {
          addAlert('Pharmacy invoice saved as unsettled', 'success');
        }
      }

      setRecipientModalOpen(false);
      setPendingFormValues(null);
      navigate('/admin/billing/invoice/patient-invoice' + (patientId ? '/' + patientId : ''));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to create pharmacy invoice or mark settlement source:', err);
      addAlert('Failed to process recipient invoice. Please try again.', 'error');
    }
  };

  /**
   * Handle direct save when all items are fully covered by package advance.
   * Creates invoice as draft, then records package advance payments.
   */
  const handlePackageFullPaySave = async () => {
    if (!formValuesForPayment) return;
    const _values = formValuesForPayment;
    const treatmentDoctorId = prescriptionsData?.data?.patient_info?.treating_doctor_id || 0;
    const visitDate = prescriptionsData?.data?.patient_info?.visit_date || getTodayYMD();
    const selectedSource = patientSourceOptions.find(src => src.value === _values.patientSource);
    const selectedTariff = selectedSource?.tariffs?.find(t => t.tariff_code === _values.tariff);
    const selectedPkg = packageOptions.find(pkg => pkg.value === _values.packageId);
    const tariffPackageInfo: TariffPackageInfo = {
      tariffId: selectedTariff?.tariff_id ?? null,
      tariffName: selectedTariff?.tariff_name,
      packageId: selectedPkg?.package?.id ?? null,
      packageName: selectedPkg?.label,
    };

    const invoicePayload = mapFormToPharmacyInvoicePayload(
      _values,
      Number(patientId),
      Number(appointmentId),
      treatmentDoctorId,
      visitDate,
      tariffPackageInfo,
      'PAID',
      false,
      selectedStoreId
    );
    if (isDonorOrSurrogate) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (invoicePayload as any).invoice_patient_type = 'donor';
    }

    try {
      let finalInvoiceId: number | undefined;
      if (invoiceId) {
        await updateInvoice.mutateAsync(
          buildUpdatePharmacyInvoiceParams(invoiceId, invoicePayload)
        );
        finalInvoiceId = Number(invoiceId);
      } else {
        const createdInvoice = await createPharmacyInvoiceMutation.mutateAsync(
          buildCreatePharmacyInvoiceParams(invoicePayload)
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const resp = createdInvoice as any;
        finalInvoiceId = resp?.id ?? resp?.invoice_id;
      }

      if (!finalInvoiceId) {
        addAlert('Failed to get invoice ID from response', 'error');
        return;
      }

      // Build package advance payments grouped by packageId
      const advanceMode = paymentModes.find(
        (mode) => mode.name.toLowerCase() === 'advance' || mode.code.toLowerCase() === 'advance'
      );
      if (!advanceMode) {
        addAlert('Unable to process package advance: Advance payment mode not configured.', 'error');
        return;
      }

      const packagePayments: PaymentHistoryItem[] = [];
      const pkgMap = new Map<number, number>();
      _values.items
        .filter((item) => item.checked && (item.packageId || _values.packageId))
        .forEach((item) => {
          const pkgId = item.packageId ? parseInt(String(item.packageId)) : parseInt(String(_values.packageId));
          if (pkgId) {
            const cur = pkgMap.get(pkgId) || 0;
            pkgMap.set(pkgId, cur + (Number(item.netAmount) || 0));
          }
        });

      pkgMap.forEach((amount, pkgId) => {
        if (amount > 0) {
          packagePayments.push({
            id: `pkg-${pkgId}-${Date.now()}`,
            method: 'Package Advance',
            amount,
            payment_mode_id: advanceMode.id,
            package_id: pkgId,
            date: formatDateYMD(new Date()),
            notes: 'Deducted from package advance',
          });
        }
      });

      // Determine payer from active package (for donor billing)
      const payerFromPackage = isDonorOrSurrogate
        && activePackagesData?.[0]?.patient_id != null
        && activePackagesData[0].patient_id > 0
        ? activePackagesData[0].patient_id
        : null;

      const paymentPayload = {
        ...buildPaymentApiPayload(
          packagePayments,
          finalInvoiceId,
          Number(patientId) || 0,
          Number(ClinicId) || 0,
          currency,
          'INVOICE',
          ''
        ),
        payment_source: 'PHARMACY',
        ...(payerFromPackage ? { payer_id: payerFromPackage, payer_type: 'recipient' } : {}),
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await createPaymentMutation.mutateAsync(buildCreatePaymentParams(paymentPayload as any));
      // Invalidate active-packages so Advance Balance Amount refreshes
      await queryClient.invalidateQueries({ queryKey: ['active-packages'] });
      addAlert('Invoice saved successfully with package payment.', 'success');
      setPackageFullPayConfirmOpen(false);
      setFormValuesForPayment(null);
      navigate('/admin/billing/invoice/patient-invoice' + (patientId ? '/' + patientId : ''));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to save invoice with package payment:', err);
      addAlert('Failed to save invoice. Please try again.', 'error');
    }
  };

  /**
   * Handle payment save from modal
   * Flow: Create invoice -> Get invoice ID -> Create payment
   */
  // Separate payment API call
  const handleCreatePayment = (invoiceId: number, data: PaymentFormValues, history: PaymentHistoryItem[], packageId?: number | null) => {
    // Build package advance payment details if there's consume from package amount
    const packagePayments: PaymentHistoryItem[] = [];
    
    if (consumeFromPackageForModal > 0 && packageId) {
      // Get the "Advance" payment mode ID 
      const advanceMode = paymentModes.find(
        (mode) => mode.name.toLowerCase() === 'advance' || mode.code.toLowerCase() === 'advance'
      );
      
      if (advanceMode) {
        packagePayments.push({
          id: `pkg-${packageId}-${Date.now()}`,
          method: 'Package Advance',
          amount: consumeFromPackageForModal,
          payment_mode_id: advanceMode.id,
          package_id: packageId,
          date: formatDateYMD(new Date()),
          notes: 'Deducted from package advance',
        });
      }
    }

    // Combine package payments with regular payment history
    const combinedHistory = [...packagePayments, ...history];

    // Ensure all payment history items have valid dates
    const validatedHistory = combinedHistory.map((item) => ({
      ...item,
      date: item.date || formatDateYMD(new Date()),
      chequeDate: item.chequeDate ? formatDateYMD(new Date(item.chequeDate)) : undefined,
    }));

    const paymentPayload = data.paymentMode === 'credit'
      ? {
        invoice_id: Number(invoiceId),
        patient_id: Number(patientId),
        clinic_id: Number(ClinicId),
        payment_source: 'PHARMACY',
        payment_against_type: 'INVOICE',
        remarks: '',
        payment_details: [], // Empty for credit mode
        ...(isDonorOrSurrogate && activePackagesData?.[0]?.patient_id
          ? { payer_id: activePackagesData[0].patient_id, payer_type: 'recipient' }
          : {}),
      }
      : {
        ...buildPaymentApiPayload(
          validatedHistory,
          Number(invoiceId),
          Number(patientId),
          Number(ClinicId),
          currency,
          'INVOICE',
          ''
        ),
        payment_source: 'PHARMACY', // Add payment_source for pharmacy
        ...(isDonorOrSurrogate && activePackagesData?.[0]?.patient_id
          ? { payer_id: activePackagesData[0].patient_id, payer_type: 'recipient' }
          : {}),
      };

    // eslint-disable-next-line no-console
    console.log('Payment API Payload:', paymentPayload);

    createPaymentMutation
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .mutateAsync(buildCreatePaymentParams(paymentPayload as any))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then(async (paymentResponse: any) => {
        // eslint-disable-next-line no-console
        console.log('Payment created:', paymentResponse);
        // Invalidate active-packages so Advance Balance Amount refreshes
        await queryClient.invalidateQueries({ queryKey: ['active-packages'] });
        setCollectPaymentOpen(false);
        // navigate(`/admin/billing/pharmacy/patient-pharmacy/${patientId}`);        
        navigate(`/admin/billing/invoice/patient-invoice/${patientId}`);
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((err: any) => {
        // eslint-disable-next-line no-console
        console.error('Failed to create payment', err);
      });
  };
  
  // Now handlePaymentSave only creates invoice, then calls payment
  const handlePaymentSave = (data: PaymentFormValues, history: PaymentHistoryItem[]) => {
    if (!formikRef.current) {
      // eslint-disable-next-line no-console
      console.error('No form values available for payment');
      return;
    }
    const values = formikRef.current.values;
    const treatmentDoctorId = prescriptionsData?.data?.patient_info?.treating_doctor_id || 0;
    const visitDate = prescriptionsData?.data?.patient_info?.visit_date || getTodayYMD();
    const selectedSource = patientSourceOptions.find(src => src.value === values.patientSource);
    const selectedTariff = selectedSource?.tariffs?.find(t => t.tariff_code === values.tariff);
    const selectedPkg = packageOptions.find(pkg => pkg.value === values.packageId);
    const tariffPackageInfo: TariffPackageInfo = {
      tariffId: selectedTariff?.tariff_id ?? null,
      tariffName: selectedTariff?.tariff_name,
      packageId: selectedPkg?.package?.id ?? null,
      packageName: selectedPkg?.label,
    };
    
    if (invoiceId) {
      const updatedPayload = mapFormToPharmacyInvoicePayload(
        values,
        Number(patientId),
        Number(appointmentId),
        treatmentDoctorId,
        visitDate,
        tariffPackageInfo,
        'PAID',
        false,
        selectedStoreId
      );
      // Add balance_amount: net_amount - paid_amount (if available)
      // You may want to get paidAmount from payment modal or history, here set to 0 for now
     
      const netAmount = updatedPayload.net_amount || 0;
      updatedPayload.balance_amount = netAmount;
      if (isDonorOrSurrogate) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (updatedPayload as any).invoice_patient_type = 'donor';
      }
      updateInvoice
        .mutateAsync(buildUpdatePharmacyInvoiceParams(invoiceId, updatedPayload))
        .then((invoiceResponse) => {
        // eslint-disable-next-line no-console
          console.log('Pharmacy invoice updated:', invoiceResponse);
          // Narrow the response type instead of using `any`
          const response = invoiceResponse as { id?: number } | undefined;
          if (!response) {
          // eslint-disable-next-line no-console
            console.error('Failed to update Invoice');
            return;
          }
          // Now call payment API separately with package ID
          handleCreatePayment(invoiceId, data, history, selectedPkg?.package?.id);
        })
        .catch((err) => {
        // eslint-disable-next-line no-console
          console.error('Failed to update  pharmacy invoice', err);
        });
      return;
      // handleCreatePayment(Number(invoiceId), data, history);
    }

    const invoicePayload = mapFormToPharmacyInvoicePayload(
      values,
      Number(patientId),
      Number(appointmentId),
      treatmentDoctorId,
      visitDate,
      tariffPackageInfo,
      'PAID',
      false,
      selectedStoreId
    );
    if (isDonorOrSurrogate) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (invoicePayload as any).invoice_patient_type = 'donor';
    }

    createPharmacyInvoiceMutation
      .mutateAsync(buildCreatePharmacyInvoiceParams(invoicePayload))
      .then((invoiceResponse) => {
        // eslint-disable-next-line no-console
        console.log('Pharmacy invoice created:', invoiceResponse);
        // Narrow the response type instead of using `any`
        const response = invoiceResponse as { id?: number } | undefined;
        const invoiceId = response?.id;
        if (!invoiceId) {
          // eslint-disable-next-line no-console
          console.error('Failed to get invoice ID from response');
          return;
        }
        // Now call payment API separately with package ID
        handleCreatePayment(invoiceId, data, history, selectedPkg?.package?.id);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Failed to create pharmacy invoice', err);
      });
  };

  /**
   * -----------------------------------------------------------------------
   * STEP 1 → STEP 2: Handle Advance Form Submission
   * -----------------------------------------------------------------------
   * Called when user completes CollectPackageAdvanceModal (Step 1).
   * Prepares payment details and opens CollectPaymentModal (Step 2).
   * 
   * @param data - Package advance form values (package selection, amount, date, remarks)
   */
  const handlePackageAdvanceCollectPayment = useCallback((data: PackageAdvanceFormData) => {
    // Store form data for Step 3 (final save)
    setPackageAdvanceFormData(data);
    
    // Close Step 1 modal 
    setCollectPackageAdvanceOpen(false);
    
    // Validate advance amount
    if (!data.advanceAmount || data.advanceAmount <= 0) {
      addAlert('Please enter a valid advance amount.', 'warning');
      return;
    }
    
    // Prepare payment details for Step 2 modal (CollectPaymentModal)
    const paymentDetails: PaymentDetails = {
      date: formatDateDMY(),
      patientName: patientFullName || 'Patient',
      patientId: patientMRN || patientId || '',
      patientNumericId: patientId,
      currency: currency || 'INR',
      netAmount: data.advanceAmount,
    };
    
    setPackageAdvancePaymentDetails(paymentDetails);
    
    // Open Step 2 modal (payment methods) after brief delay for smooth transition
    // Clear any existing timer before setting a new one
    if (modalTransitionTimerRef.current) {
      clearTimeout(modalTransitionTimerRef.current);
    }
    modalTransitionTimerRef.current = setTimeout(() => {
      setCollectPackagePaymentOpen(true);
      modalTransitionTimerRef.current = null;
    }, 150);
  }, [patientFullName, patientId, patientMRN, currency, addAlert]);

  /**
   * -----------------------------------------------------------------------
   * STEP 3: SAVE PACKAGE ADVANCE  
   * -----------------------------------------------------------------------
   * Called when user completes payment in CollectPaymentModal (Step 2).
   * This is the final step that saves the package advance to the backend.
   * 
   * @param data - Payment form values (payment mode selections, amounts)
   * @param history - Array of payment history items (method, amount, date, etc.)
   */
  const handleSavePackageAdvance = useCallback((data: PaymentFormValues, history: PaymentHistoryItem[]) => {
    // Validate packageAdvanceFormData exists
    if (!packageAdvanceFormData) {
      // eslint-disable-next-line no-console
      console.error('handleSavePackageAdvance: packageAdvanceFormData is null');
      addAlert('Package advance data not found. Please try again.', 'error');
      setCollectPackagePaymentOpen(false);
      return;
    }

    // Validate payment history is not empty
    if (!history || history.length === 0) {
      addAlert('Please add at least one payment method.', 'warning');
      return;
    }

    // Calculate total payment amount from history
    const totalPaymentAmount = history.reduce((sum, item) => sum + (item.amount || 0), 0);
    
    // Validate payment amount matches advance amount
    if (Math.abs(totalPaymentAmount - packageAdvanceFormData.advanceAmount) > 0.01) {
      addAlert(
        `Payment amount (${totalPaymentAmount}) does not match advance amount (${packageAdvanceFormData.advanceAmount}).`,
        'warning'
      );
      return;
    }

    // Calculate balance amount (package amount - already collected advance)
    const balanceAmount = Math.max(
      (packageAdvanceFormData.packageAmount ?? 0) - (packageAdvanceFormData.packageAdvanceCollected ?? 0),
      0
    );

    // Build payment details array
    const paymentCurrency = currency || 'INR';

    const payment_details: PaymentDetailPayload[] = history.map((item) => {
      const base: PaymentDetailPayload = {
        payment_mode_id: item.payment_mode_id ?? 0,
        paid_amount: String(item.amount ?? 0),
        currency: paymentCurrency,
        number: null,
        date: toISODateString(item.date),
        bank_id: null,
        account_name: null,
        branch_name: null,
        ifsc_code: null,
        remarks: item.notes ?? packageAdvanceFormData.remark ?? null,
        cheque_number: null,
        cheque_date: null,
      };

      // Map transaction numbers based on payment method
      if (item.method?.toLowerCase().includes('bank') || 
          item.method?.toLowerCase().includes('online') || 
          item.method?.toLowerCase().includes('card') || 
          item.method?.toLowerCase().includes('upi')) {
        base.number = item.transactionNumber ?? item.cardTransactionNumber ?? null;
      }

      // Map cheque-specific fields
      if (item.method?.toLowerCase().includes('cheque')) {
        base.cheque_number = item.chequeNumber ?? null;
        base.cheque_date = item.chequeDate ? toISODateString(item.chequeDate) : null;
      }

      return base;
    });

    // Validate required IDs before creating payload
    if (!patientId || !ClinicId) {
      addAlert('Missing patient or clinic information. Please try again.', 'error');
      return;
    }

    // Build the advance payment payload
    const payload: MakePayment = {
      invoice_id: null, // null for advance payments
      patient_id: Number(patientId),
      clinic_id: Number(ClinicId),
      payment_against_type: 'ADVANCE',
      advance_data: {
        advance_against: packageAdvanceFormData.advanceAgainst || 'Package Service',
        advance_type: 'PACKAGE',
        date: packageAdvanceFormData.advanceDate || getTodayYMD(),
        amount: totalPaymentAmount,
        remark: packageAdvanceFormData.remark || '',
        appointment_visit: appointmentId ?? 0,
        patient_package_id: packageAdvanceFormData.selectedPackageId,
        balance_amount: balanceAmount,
      },
      remarks: packageAdvanceFormData.remark || '',
      payment_details,
    };

    // Log for debugging (development only)
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('Package Advance Payment Payload:', payload);
    }

    // Submit the advance payment
    submitAdvancePayment(payload);
  }, [packageAdvanceFormData, addAlert, currency, patientId, ClinicId, appointmentId, submitAdvancePayment]);

  /**
   * -----------------------------------------------------------------------
   * OPEN COLLECT PACKAGE ADVANCE MODAL (External Handler)
   * -----------------------------------------------------------------------
   * Called from InsufficientAdvancePopup's onConfirm callback.
   * Fetches fresh package data and opens the CollectPackageAdvanceModal (Step 1).
   */
  const handleOpenCollectPackageAdvanceExternal = useCallback(async () => {
    // Refetch active packages to get fresh data from API
    const { data: freshPackages } = await refetchActivePackages();
    
    // Validate that freshPackages data exists and is an array with items
    if (!freshPackages || !Array.isArray(freshPackages) || freshPackages.length === 0) {
      addAlert('No active packages found for this patient. Please create a package first.', 'warning');
      return;
    }

    // Map ALL packages from API response to PackageOption format
    const packageOptionsForModal: AdvancePackageOption[] = freshPackages.map((pkg) => {
      // Find matching validation result for this package
      const validationPkg = validationResultPackages.find((vp) => vp.package_id === pkg.id);
      // Calculate required advance: required_amount - balance_amount (what's still needed)
      const requiredAdvance = validationPkg 
        ? Math.max(0, validationPkg.required_amount - validationPkg.balance_amount)
        : undefined;
      
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('Package mapping:', {
          packageId: pkg.id,
          validationPkg,
          requiredAdvance,
        });
      }

      return {
        id: pkg.id,
        name: pkg.package_name || 'Unknown Package',
        startDate: pkg.start_date ?? undefined,
        packageAmount: pkg.package_amount ?? 0,
        packageNetAmount: Number(pkg.package_net_amount ?? 0),
        packageConcessionAmount: Number(pkg.package_concession_amount ?? 0),
        totalServiceConcessionAmount: Number(pkg.total_service_concession_amount ?? 0),
        packageAdvanceCollected: pkg.package_advance_collected ?? 0,
        advanceCollected: pkg.advance_collected ?? 0,
        packageUsedAmount: pkg.package_used_amount ?? 0,
        packageBalanceAmount: pkg.package_balance_amount ?? 0,
        serviceRemainingAmount: (pkg.service_remaining_amount ?? 0) - Number(pkg.package_concession_amount ?? 0) - Number(pkg.total_service_concession_amount ?? 0),
        pharmacyRemainingAmount: pkg.pharmacy_remaining_amount ?? 0,
        serviceAmount: (pkg.service_amount ?? 0) - Number(pkg.package_concession_amount ?? 0) - Number(pkg.total_service_concession_amount ?? 0),
        pharmacyAmount: pkg.pharmacy_amount ?? 0,
        pharmacyComponentAmount: pkg.pharmacy_component_amount ?? 0,
        pharmacyComponentPercent: pkg.pharmacy_component_percent ?? 0,
        collectedPharmacyAdvanceAmount: pkg.collected_pharmacy_advance_amount ?? 0,
        remainingPharmacyAdvanceAmount: pkg.remaining_pharmacy_advance_amount ?? 0,
        requiredAdvanceAmount: requiredAdvance,
      };
    });

    // Set packages and open Step 1 modal
    setPackageOptionsForAdvance(packageOptionsForModal);
    setCollectPackageAdvanceOpen(true);
  }, [refetchActivePackages, addAlert, validationResultPackages]);


  /**
   * -----------------------------------------------------------------------
   * Effects – initialize dropdown options and auto-select
   * -----------------------------------------------------------------------
   */

  // Map billing config -> dropdown option lists
  useEffect(() => {
    if (!billingConfigData) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const configData = billingConfigData as any;
    const { tariff_source_group, CONCESSION_REASON } = configData;

    if (tariff_source_group?.length) {
      setPatientSourceOptions(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tariff_source_group.map((item: any) => ({
          label: item.label ?? item.name,
          value: item.value ?? item.code,
          tariffs: item.tariffs ?? [],
        }))
      );
    }

    if (CONCESSION_REASON?.length) {
      setConcessionReasonOptions(CONCESSION_REASON);
    }

    // Extract payment modes
    const modeofpayment = configData?.modeofpayment || configData?.PAYMENT_MODE;
    if (modeofpayment?.length) {
      setPaymentModes(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        modeofpayment.map((item: any) => ({
          id: item.id,
          name: item.name,
          code: String(item.code),
        }))
      );
    }
  }, [billingConfigData]);

  // Map active packages data -> dropdown option lists
  useEffect(() => {
    if (activePackagesError) {
      setPackageOptions([]);
      return;
    }

    if (!activePackagesData?.length) {
      setPackageOptions([]);
      return;
    }

    // Map API PackageDetails -> PackageOption expected by PackageSelect
    const mappedOptions = activePackagesData.map((p: PackageDetails) => ({
      value: String(p.id),
      label: p.package_name,
      package: p as unknown as Package,  // PackageDetails has id and package_name, compatible with PackageOption
    }));

    setPackageOptions(mappedOptions);
  }, [activePackagesData, activePackagesError]);

  // Auto-select first patient source
  useEffect(() => {
    if (!formikRef.current) return;
    if (!formikRef.current.values.patientSource && patientSourceOptions.length > 0) {
      const firstSource = patientSourceOptions[0];
      formikRef.current.setFieldValue('patientSource', firstSource.value, false);
      // Mark as touched to avoid validation errors
      formikRef.current.setFieldTouched('patientSource', true, false);
    }
  }, [patientSourceOptions]);

  // Auto-select first tariff when patient source changes
  useEffect(() => {
    if (!formikRef.current) return;
    const currentSource = formikRef.current.values.patientSource;
    const selectedSource = patientSourceOptions.find(
      (src) => src.value === currentSource
    );
    const tariffOptions = selectedSource?.tariffs || [];
    
    if (currentSource && !formikRef.current.values.tariff && tariffOptions.length > 0) {
      formikRef.current.setFieldValue('tariff', tariffOptions[0].tariff_code, false);
      // Mark as touched to avoid validation errors
      formikRef.current.setFieldTouched('tariff', true, false);
    }
  }, [formikRef.current?.values.patientSource, patientSourceOptions]);

  // Helper function to map prescription to form item
  const mapPrescriptionToFormItem = (prescription: PrescriptionItem): FormPharmacyItem => {
    // Map available batches to the form format
    const availableBatches = (prescription.available_batches || []).map((batch) => {
      // Build taxes array from batch-level tax data
      // Supports both flat fields (sgst_percent) and nested taxes object ({ sgst: { percent } })
      const batchTaxes: Array<{ name: string; percent: number; amount: number }> = [];
      const sgstPct = batch.sgst_percent ?? batch.taxes?.sgst?.percent ?? 0;
      const cgstPct = batch.cgst_percent ?? batch.taxes?.cgst?.percent ?? 0;
      const igstPct = batch.taxes?.igst?.percent ?? 0;
      if (sgstPct > 0) batchTaxes.push({ name: 'SGST', percent: sgstPct, amount: 0 });
      if (cgstPct > 0) batchTaxes.push({ name: 'CGST', percent: cgstPct, amount: 0 });
      if (igstPct > 0) batchTaxes.push({ name: 'IGST', percent: igstPct, amount: 0 });
      return {
        batchId: batch.id ?? batch.batch_id,
        batchNumber: batch.batch_number,
        expiryDate: formatDateDMY(batch.expiry_date),
        isExpired: batch.is_expired,
        baseMrp: batch.base_mrp,
        baseCostPrice: batch.base_cost_price,
        availableStock: batch.available_stock,
        availableUomId: batch.available_uom_id,
        availableUom: batch.available_uom || batch.available_uom_name,
        storeId: batch.store_id,
        taxes: batchTaxes,
      };
    });

    // Find the selected batch from ORIGINAL available_batches array
    const selectedBatchCode = prescription.batch_code;
    const selectedBatchNumber = prescription.batch_number;
    const selectedBatchId = prescription.batch_id;
    

    
    const originalSelectedBatch = (prescription.available_batches || []).find((b) => {

      // Match by batch_number against prescription.batch_code (most common case)
      if (selectedBatchCode && b.batch_number && 
          b.batch_number.toLowerCase() === selectedBatchCode.toLowerCase()) {

        return true;
      }
      // Match by batch_number against prescription.batch_number
      if (selectedBatchNumber && b.batch_number && 
          b.batch_number.toLowerCase() === selectedBatchNumber.toLowerCase()) {

        return true;
      }
      // Match by ID
      if (selectedBatchId && (b.id === selectedBatchId || b.batch_id === selectedBatchId)) {

        return true;
      }
      return false;
    });
    

    
    // Use ONLY values from selected batch; if not found, log warning and use prescription values as fallback
    if (!originalSelectedBatch) {
      // eslint-disable-next-line no-console
      console.warn(
        `Selected batch not found for prescription ${String(prescription.prescription_id ?? '')} ` +
        `(item: ${String(prescription.item_name ?? prescription.item_code ?? '')}, ` +
        `batchCode: ${String(selectedBatchCode ?? '')}, batchNumber: ${String(selectedBatchNumber ?? '')}, ` +
        `batchId: ${String(selectedBatchId ?? '')}). Using prescription values as fallback.`
      );
    }
    const baseMRP = originalSelectedBatch?.base_mrp ?? prescription.base_mrp ?? 0;
    const baseCostPrice = originalSelectedBatch?.base_cost_price ?? prescription.base_cost_price ?? 0;
    

    
    const billedQty = prescription.billed_quantity || prescription.billed_qty || 0;
    return {
      id: prescription.id,
      prescriptionId: prescription.prescription_id,
      itemId: prescription.item_id,
      visitDate: formatDateDMY(prescription.visit_date),
      itemCode: prescription.item_code,
      itemName: prescription.item_name,
      batchCode: prescription.batch_code || prescription.batch_number || '',
      batchId: prescription.batch_id ?? undefined,
      expiryDate: prescription.expiry_date || '',
      manufacturer: prescription.manufacturer || '',
      availableBatches: availableBatches,
      prescribedStrength: prescription.prescribed_strength || prescription.strength || '',
      prescribedQty: prescription.prescribed_quantity ?? prescription.prescribed_qty ?? 0,
      prescribedUOM: prescription.prescribed_uom,
      billedStrength: prescription.billed_strength || '',
      billedQty: billedQty,
      billedUOM: prescription.billed_uom || prescription.billed_uom_code || '',
      frequency: ((prescription as PrescriptionItem & { frequency?: string }).frequency as FormPharmacyItem['frequency']) || '1-0-1',
      rack: prescription.rack ?? '',
      shelf: prescription.shelf ?? '',
      container: prescription.container ?? '',
      checked: true,
      baseMRP: baseMRP ?? 0,
      baseCostPrice: baseCostPrice ?? 0,
      totalMRPAmount: prescription.total_mrp_amount ?? (baseMRP * billedQty),
      concessionAmount: prescription.concession_amount ?? prescription.discount_amount ?? 0,
      concessionPercent: prescription.concession_percentage ?? 0,
      // Build taxes array from prescription tax data
      taxes: (() => {
        const taxes: Array<{ name: string; percent: number; amount: number }> = [];
        const sgstPct = prescription.sgst_percent_invoice ?? 0;
        const cgstPct = prescription.cgst_percent_invoice ?? 0;
        const sgstAmt = prescription.sgst_amount_invoice ?? 0;
        const cgstAmt = prescription.cgst_amount_invoice ?? 0;
        if (sgstPct > 0 || sgstAmt > 0) taxes.push({ name: 'SGST', percent: sgstPct, amount: sgstAmt });
        if (cgstPct > 0 || cgstAmt > 0) taxes.push({ name: 'CGST', percent: cgstPct, amount: cgstAmt });
        return taxes;
      })(),
      netAmount: prescription.net_amount ?? prescription.total_mrp_amount ?? (baseMRP * billedQty),
      pregnancyClass: prescription.pregnancy_class ?? prescription.pregnancy_class_code ?? '',
      provider: prescription.provider ?? prescription.prescribed_by ?? '',
      availableStock: originalSelectedBatch?.available_stock ?? prescription.available_stock ?? 0,
      availableUomId: originalSelectedBatch?.available_uom_id ?? prescription.available_uom_id,
      availableUom: originalSelectedBatch?.available_uom_name ?? prescription.available_uom,
      storeId: prescription.store_id ?? undefined,
      // Tariff and Package info — set from package_details if item is from a package
      tariffId: null,
      tariffName: undefined,
      // Package info from prescription's package_details
      packageId: prescription.package_details?.[0]?.package_id ?? null,
      packageName: prescription.package_details?.[0]?.package_name ?? undefined,
      is_fromPackage: !!(prescription.package_details && prescription.package_details.length > 0),
      package_pharmacy_item_id: prescription.package_details?.[0]?.package_pharmacy_item_id ?? undefined,
      package_pending_qty: prescription.package_details?.[0]?.quantity_remaining ?? undefined,
      package_total_qty: prescription.package_details?.[0]?.total_quantity ?? undefined,
      package_details: prescription.package_details || [],
      uomConversions: prescription.uom_conversions || prescription.uom_object ||[],
      prescribed_remaining_qty: prescription.remaining_quantity ?? prescription.remaining_qty,
    };
  };

  // Auto-populate items from selectedDrugs (By Pharmacy tab)
  // Uses invoice-details-by-items API to get batch data, falling back to basic mapping
  useEffect(() => {
    if (!formikRef.current || !selectedDrugs?.length || prescriptionsLoaded) return;

    // Wait for store to be determined (set asynchronously from localStorage)
    if (!selectedStoreId) return;

    // Wait for API to finish loading
    if (isSelectedPrescriptionsLoading) return;

    // If API data with batches is available, use the dispense-style mapper
    if (selectedPrescriptionsData?.items?.length) {
      const mappedItems = selectedPrescriptionsData.items.map(mapSelectedPrescriptionToFormItem);
      formikRef.current.setFieldValue('items', mappedItems, false);
      setPrescriptionsLoaded(true);
      return;
    }

    // Fallback: API returned no items, use basic mapping without batch data
    const mappedItems = selectedDrugs.map(mapPatientDrugToFormItem);
    formikRef.current.setFieldValue('items', mappedItems, false);
    // Set due date to 30 days from today for dispensed items
    formikRef.current.setFieldValue('dueDate', dispensedDueDate, false);
    setPrescriptionsLoaded(true);
  }, [selectedDrugs, prescriptionsLoaded, dispensedDueDate, selectedPrescriptionsData, selectedStoreId, isSelectedPrescriptionsLoading]);

  // Auto-populate items from current_prescriptions when data loads
  useEffect(() => {
    if (!formikRef.current || !prescriptionsData || prescriptionsLoaded) return;
    
    const currentPrescriptions = prescriptionsData?.data?.current_prescriptions || [];
    if (currentPrescriptions.length > 0) {
      const mappedItems = currentPrescriptions.map(mapPrescriptionToFormItem);
      formikRef.current.setFieldValue('items', mappedItems, false);
      setPrescriptionsLoaded(true);
    }
  }, [prescriptionsData, prescriptionsLoaded]);

  // Separately handle package selection from package_details when packageOptions are available
  useEffect(() => {
    if (!formikRef.current || !prescriptionsData || !packageOptions.length) return;
    
    const packageDetails = prescriptionsData?.data?.package_details;
    // eslint-disable-next-line no-console
    console.log('Package matching effect - packageDetails:', packageDetails);
    // eslint-disable-next-line no-console
    console.log('Package matching effect - packageOptions:', packageOptions);
    
    if (packageDetails?.package_id) {
      // Find the matching package option by ID
      const matchingOption = packageOptions.find((opt) => {
        // eslint-disable-next-line no-console
        console.log(`Comparing: opt.package?.id=${opt.package?.id} vs packageDetails.package_id=${packageDetails.package_id}`);
        return opt.package?.id === packageDetails.package_id;
      });
      
      // eslint-disable-next-line no-console
      console.log('Matching option found:', matchingOption);
      
      if (matchingOption && !formikRef.current.values.packageId) {
        // eslint-disable-next-line no-console
        console.log('Setting packageId from package_details:', matchingOption.value, 'Name:', matchingOption.package.package_name);
        formikRef.current.setFieldValue('packageId', matchingOption.value, false);
      }
    }
  }, [prescriptionsData, packageOptions]);

  /**
   * -----------------------------------------------------------------------
   * Submit handler
   * -----------------------------------------------------------------------
   */

  const handleSubmitForm = (_values: PharmacyFormValues) => {
    // eslint-disable-next-line no-console
    console.log('handleSubmitForm called with values:', _values);
    
    // Validate we have required IDs
    if (!patientId || !appointmentId) {
      // eslint-disable-next-line no-console
      console.error('Missing patientId or appointmentId', { patientId, appointmentId });
      alert(`Missing required IDs: patientId=${patientId}, appointmentId=${appointmentId}`);
      return;
    }

    // Check if there are selected items
    const selectedItems = _values.items.filter((item) => item.checked);
    if (selectedItems.length === 0) {
      // eslint-disable-next-line no-console
      console.warn('No items selected for billing');
      alert('Please select at least one item for billing');
      return;
    }

    // eslint-disable-next-line no-console
    console.log('Selected items:', selectedItems);

    // Get treatment doctor ID and visit date from prescription data
    const treatmentDoctorId = prescriptionsData?.data?.patient_info?.treating_doctor_id || 0;
    const visitDate = prescriptionsData?.data?.patient_info?.visit_date || getTodayYMD();

    // Build tariff and package info from form values
    const selectedSource = patientSourceOptions.find(src => src.value === _values.patientSource);
    const selectedTariff = selectedSource?.tariffs?.find(t => t.tariff_code === _values.tariff);
    const selectedPkg = packageOptions.find(pkg => pkg.value === _values.packageId);
    
    const tariffPackageInfo: TariffPackageInfo = {
      tariffId: selectedTariff?.tariff_id ?? null,
      tariffName: selectedTariff?.tariff_name,
      packageId: selectedPkg?.package?.id ?? null,
      packageName: selectedPkg?.label,
    };

    // Map form values to API payload
    const payload = mapFormToPharmacyInvoicePayload(
      _values,
      Number(patientId),
      appointmentId,
      treatmentDoctorId,
      visitDate,
      tariffPackageInfo,
      undefined,
      false,
      selectedStoreId
    );

    // eslint-disable-next-line no-console
    console.log('Creating pharmacy invoice with payload:', payload);

    // Call the API using promises
    createPharmacyInvoiceMutation
      .mutateAsync(buildCreatePharmacyInvoiceParams(payload))
      .then((response) => {
        // eslint-disable-next-line no-console
        console.log('Pharmacy invoice created successfully:', response);

        // Navigate back to pharmacy list on success
        navigate('/admin/billing/pharmacy');
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to create pharmacy invoice:', error);
        alert(`Failed to create pharmacy invoice: ${error.message || 'Unknown error'}`);
      });
  };

  // Batch selection modal state
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [selectedItemForBatch, setSelectedItemForBatch] = useState<ApiPharmacyItem | null>(null);
  const [pendingSetFieldValue, setPendingSetFieldValue] = useState<((field: string, value: unknown) => void) | null>(null);
  const [pendingCurrentItems, setPendingCurrentItems] = useState<FormPharmacyItem[]>([]);
  
  // Add ref to access ItemSearchField's clear function
  const itemSearchFieldRef = useRef<{ clearSearch: () => void } | null>(null);

  /**
   * Handle item selection from search - now opens batch modal
   */
  const handleSelectItemFromSearch = (
    apiItem: ApiPharmacyItem,
    setFieldValue: (field: string, value: unknown) => void,
    currentItems: FormPharmacyItem[]
  ) => {
    // Store the item and open batch selection modal
    setSelectedItemForBatch(apiItem);
    setPendingSetFieldValue(() => setFieldValue);
    setPendingCurrentItems(currentItems);
    setBatchModalOpen(true);
  };

  /**
   * Handle batch selection from modal
   */
  const handleBatchSelect = (batch: PharmacyItemBatch) => {
    if (!selectedItemForBatch || !pendingSetFieldValue) return;

    // Build taxes array from batch-level tax percentages
    const baseMRP = batch.base_mrp || 0;
    const billedQty = 1;
    const baseTaxes: Array<{ name: string; percent: number; amount: number }> = [];
    const sgstPct = batch.sgst_percent ?? selectedItemForBatch.SGST ?? 0;
    const cgstPct = batch.cgst_percent ?? selectedItemForBatch.CGST ?? 0;
    if (sgstPct > 0) baseTaxes.push({ name: 'SGST', percent: sgstPct, amount: 0 });
    if (cgstPct > 0) baseTaxes.push({ name: 'CGST', percent: cgstPct, amount: 0 });
    
    // Calculate using tax-inclusive MRP formula
    const taxCalc = calculateFromInclusiveMRP(baseMRP, billedQty, baseTaxes);

    // Map available batches from the selected item with taxes array
    const availableBatches = (selectedItemForBatch.batches || []).map((b) => {
      const bTaxes: Array<{ name: string; percent: number; amount: number }> = [];
      const bSgst = b.sgst_percent ?? selectedItemForBatch.SGST ?? 0;
      const bCgst = b.cgst_percent ?? selectedItemForBatch.CGST ?? 0;
      if (bSgst > 0) bTaxes.push({ name: 'SGST', percent: bSgst, amount: 0 });
      if (bCgst > 0) bTaxes.push({ name: 'CGST', percent: bCgst, amount: 0 });
      return {
        batchId: b.id,
        batchNumber: b.batch_number,
        expiryDate: formatDateDMY(b.expiry_date),
        isExpired: new Date(b.expiry_date) < new Date(),
        baseMrp: b.base_mrp,
        baseCostPrice: b.base_cost,
        taxes: bTaxes,
        availableStock: b.available_stock,
        availableUomId: b.available_uom_id,
        availableUom: b.available_uom_name ?? selectedItemForBatch.base_uom_name,
        storeId: b.store_id,
      };
    });

    // Map API item with selected batch to form item structure
    const newItem: FormPharmacyItem = {
      prescriptionId: 0, // Items from search don't have prescription_id
      itemId: selectedItemForBatch.item_id,
      visitDate: formatDateDMY(new Date()),
      itemCode: selectedItemForBatch.item_code,
      itemName: selectedItemForBatch.item_name,
      batchCode: batch.batch_number,
      batchId: batch.id,
      expiryDate: formatDateDMY(batch.expiry_date),
      manufacturer: selectedItemForBatch.item_group_name || '',
      availableBatches: availableBatches,
      // Prescribed values - these would come from prescription in real scenario
      prescribedStrength: '500 Mg', // TODO: Get from prescription data
      prescribedQty: 24, // TODO: Get from prescription data
      prescribedUOM: selectedItemForBatch.base_uom_name,
      // Billed values - editable by user
      billedStrength: '',
      billedQty: billedQty,
      billedUOM: selectedItemForBatch.base_uom_name,
      frequency: '1-0-1',
      rack: selectedItemForBatch.rack_name,
      shelf: selectedItemForBatch.shelf_name,
      container: selectedItemForBatch.bin_name,
      checked: true,
      baseMRP: baseMRP,
      baseCostPrice: batch.base_cost_price || 0,
      totalMRPAmount: baseMRP * billedQty,
      sellingPricePerUnit: taxCalc.sellingPricePerUnit,
      totalSellingPrice: taxCalc.totalSellingPrice,
      concessionAmount: 0,
      concessionPercent: 0,
      taxes: taxCalc.taxesWithAmounts,
      netAmount: taxCalc.netAmount,
      pregnancyClass: '',
      provider: '',
      availableStock: batch.available_stock,
      availableUomId: batch.available_uom_id,
      availableUom: batch.available_uom_name ?? selectedItemForBatch.base_uom_name,
      storeId: batch.store_id,
      // Tariff and Package info will be set from form values at submit time
      tariffId: null,
      tariffName: undefined,
      packageId: null,
      packageName: undefined,
      // Package details from API (available when patient_id is passed)
      package_details: selectedItemForBatch.package_details?.map(pkg => ({
        ...pkg,
        is_set_all_items: pkg.is_set_all_items ?? false,
      })) as PackageDetail[] | undefined,
      // UOM conversions from item (needed for UOM dropdown in table)
      uomConversions: selectedItemForBatch.uom_conversions?.map((u) => ({
        id: u.id,
        item_id: u.item_id,
        item_name: u.item_name,
        uom_id: u.uom_id,
        uom_code: u.uom_code,
        code: u.code,
        uom_static_field: u.uom_static_field || u.uom_code,
        conversion_factor: u.conversion_factor,
        converted_mrp: u.converted_mrp,
        converted_cost_price: u.converted_cost_price,
      })),
    };

    // Auto-select package if item belongs to exactly one package
    const itemPackageDetails = newItem.package_details;
    if (itemPackageDetails && itemPackageDetails.length > 0) {
      const firstPkg = itemPackageDetails[0];
      newItem.packageId = firstPkg.package_id;
      newItem.packageName = firstPkg.package_name;
      newItem.is_fromPackage = true;
      newItem.package_pharmacy_item_id = firstPkg.package_pharmacy_item_id ?? undefined;
      newItem.package_pending_qty = firstPkg.quantity_remaining ?? undefined;
      newItem.package_total_qty = firstPkg.total_quantity ?? undefined;
      newItem.uom_description = firstPkg.uom_description ?? undefined;

      // Also set the global form packageId if not already selected
      if (pendingSetFieldValue && !formikRef.current?.values.packageId) {
        const matchingOption = packageOptions.find(
          (opt) => opt.package?.id === firstPkg.package_id
        );
        if (matchingOption) {
          pendingSetFieldValue('packageId', matchingOption.value);
          setSelectedPackage(matchingOption.package ?? undefined);
        }
      }
    }

    pendingSetFieldValue('items', [...pendingCurrentItems, newItem]);
    
    // Reset state and clear search box
    setBatchModalOpen(false);
    setSelectedItemForBatch(null);
    setPendingSetFieldValue(null);
    setPendingCurrentItems([]);
    
    // Clear the search box
    if (itemSearchFieldRef.current) {
      itemSearchFieldRef.current.clearSearch();
    }
  };

  /**
   * Handle closing batch modal without selection
   */
  const handleBatchModalClose = () => {
    setBatchModalOpen(false);
    setSelectedItemForBatch(null);
    setPendingSetFieldValue(null);
    setPendingCurrentItems([]);
    
    // Clear the search box
    if (itemSearchFieldRef.current) {
      itemSearchFieldRef.current.clearSearch();
    }
  };

  /**
   * -----------------------------------------------------------------------
   * Formik wrapper
   * -----------------------------------------------------------------------
   */

  return (
    <>
      <Formik<PharmacyFormValues>
        initialValues={{
          ...initialPharmacyValues,
          dueDate: (selectedDrugs?.length || invoiceId) ? dispensedDueDate : maxDueDate,
        }}
        validationSchema={validationPharmacySchema}
        enableReinitialize
        validateOnBlur={true}
        validateOnChange={false}
        onSubmit={(values, helpers) => {
          // eslint-disable-next-line no-console
          console.log('values--------------', values);
          
          setShowValidationErrors(true);
          handleSubmitForm(values);
          helpers.setSubmitting(false);
        }}
      >
        {({ values, setFieldValue, setFieldTouched, setTouched, handleSubmit, isSubmitting, errors, touched, validateForm }) => {
          // Store Formik's setFieldValue, setFieldTouched and values in ref
          formikRef.current = { setFieldValue, setFieldTouched, values };

          /**
           * Handle opening collect package advance modal (Step 1)
           * Calls the API fresh to get latest active packages data
           */
          const handleOpenCollectPackageAdvance = async () => {
            // Refetch active packages to get fresh data from API
            const { data: freshPackages } = await refetchActivePackages();
            
            // Use freshly fetched data
            if (!freshPackages || !Array.isArray(freshPackages) || freshPackages.length === 0) {
              addAlert('No active packages found for this patient', 'warning');
              return;
            }

            // Map ALL packages from API response to PackageOption format
            const packageOptionsForModal: AdvancePackageOption[] = freshPackages.map((pkg) => {
              // Find matching validation result for this package
              const validationPkg = validationResultPackages.find((vp) => vp.package_id === pkg.id);
              // Calculate required advance: required_amount - balance_amount (what's still needed)
              const requiredAdvance = validationPkg 
                ? Math.max(0, validationPkg.required_amount - validationPkg.balance_amount)
                : undefined;

              return {
                id: pkg.id,
                name: pkg.package_name || 'Unknown Package',
                startDate: pkg.start_date ?? undefined,
                packageAmount: pkg.package_amount ?? 0,
                packageNetAmount: Number(pkg.package_net_amount ?? 0),
                packageConcessionAmount: Number(pkg.package_concession_amount ?? 0),
                totalServiceConcessionAmount: Number(pkg.total_service_concession_amount ?? 0),
                packageAdvanceCollected: pkg.package_advance_collected ?? 0,
                advanceCollected: pkg.advance_collected ?? 0,
                packageUsedAmount: pkg.package_used_amount ?? 0,
                packageBalanceAmount: pkg.package_balance_amount ?? 0,
                serviceRemainingAmount: (pkg.service_remaining_amount ?? 0) - Number(pkg.package_concession_amount ?? 0) - Number(pkg.total_service_concession_amount ?? 0),
                pharmacyRemainingAmount: pkg.pharmacy_remaining_amount ?? 0,
                serviceAmount: (pkg.service_amount ?? 0) - Number(pkg.package_concession_amount ?? 0) - Number(pkg.total_service_concession_amount ?? 0),
                pharmacyAmount: pkg.pharmacy_amount ?? 0,
                pharmacyComponentAmount: pkg.pharmacy_component_amount ?? 0,
                pharmacyComponentPercent: pkg.pharmacy_component_percent ?? 0,
                collectedPharmacyAdvanceAmount: pkg.collected_pharmacy_advance_amount ?? 0,
                remainingPharmacyAdvanceAmount: pkg.remaining_pharmacy_advance_amount ?? 0,
                requiredAdvanceAmount: requiredAdvance,
              };
            });

            setPackageOptionsForAdvance(packageOptionsForModal);
            setCollectPackageAdvanceOpen(true);
          };

          // build tariff options from selected patientSource
          const currentSource = values.patientSource;
          const selectedSource = patientSourceOptions.find(
            (src) => src.value === currentSource
          );
          const tariffOptions: Option[] = selectedSource?.tariffs
            ? selectedSource.tariffs.map((t) => ({
              label: t.tariff_name,
              value: t.tariff_code,
            }))
            : [];

          const tariffPlaceholder =
            !currentSource
              ? 'Please assign a tariff plan by selecting a source first'
              : tariffOptions.length === 0
                ? 'No tariff plans are configured for the selected source'
                : '';

          // Get selected tariff ID for item search
          const selectedTariff = selectedSource?.tariffs?.find(t => t.tariff_code === values.tariff);
          const selectedTariffId = selectedTariff?.tariff_id ?? null;

          // Extract previous prescriptions from API response
          const previousPrescriptions = prescriptionsData?.data?.previous_prescriptions || [];

          // -------------------------------------------------------------------
          // Package Limit Validation - check if pharmacy items exceed limit
          // -------------------------------------------------------------------
          // Calculate package pharmacy items total (items linked to a package)
          const packagePharmacyTotal = values.items
            .filter((item) => item.checked && (item.packageId || values.packageId))
            .reduce((sum, item) => sum + (Number(item.netAmount) || 0), 0);
          
          // Get the pharmacy_remaining_amount from active packages
          const totalPharmacyRemaining = activePackagesData?.reduce(
            (sum, pkg) => sum + (pkg.pharmacy_remaining_amount ?? 0), 0
          ) ?? 0;
          
          //////////////////////////////////////////////////////////////////////////
          //
          // TODO: [MULTI-PACKAGE-SUPPORT]
          // Update to sum advance balance across all active packages.
          // Currently simplified to use only the first package's advance balance.
          // When multiple packages are supported, change to:
          //
          // const totalAdvanceBalance = activePackagesData?.reduce(
          //   (sum, pkg) => sum + (pkg.package_balance_amount ?? 0), 0
          // ) ?? 0;
          //
          //////////////////////////////////////////////////////////////////////////
          const totalAdvanceBalance = activePackagesData?.[0]?.package_balance_amount ?? 0;
          
          // Check if package pharmacy limit is exceeded (blocks payment)
          const isPackageLimitExceeded = packagePharmacyTotal > 0 && totalPharmacyRemaining > 0 && packagePharmacyTotal > totalPharmacyRemaining;

          // Cap the consume amount at the available pharmacy_remaining_amount
          const consumeFromPackageAmount = Math.min(packagePharmacyTotal, totalPharmacyRemaining);

          // Check if ALL items are from package and fully covered by advance
          const selectedItems = values.items.filter((item) => item.checked);
          const allItemsFromPackage = selectedItems.length > 0 &&
            selectedItems.every((item) => !!(item.packageId || values.packageId) && item.is_fromPackage);
          const netAmount = selectedItems.reduce((sum, item) => sum + item.netAmount, 0);
          const isFullyCoveredByPackage = allItemsFromPackage &&
            consumeFromPackageAmount >= netAmount &&
            totalAdvanceBalance >= consumeFromPackageAmount;

          // True when any checked item has a concession amount or percent > 0
          const hasConcession = values.items.some(
            (item) =>
              item.checked &&
              ((item.concessionAmount ?? 0) > 0 ||
               (item.concessionPercent ?? 0) > 0)
          );

          return (
            <form onSubmit={handleSubmit}>
              <ConcessionReasonAutoClear
                hasConcession={hasConcession}
                concessionReason={values.concessionReason}
                setFieldValue={setFieldValue}
              />
              {/* High-level loading / error banners */}
              {isConfigLoading && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Loading billing configuration...
                </Typography>
              )}
              {isConfigError && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  Error loading config: {configError?.message}
                </Typography>
              )}

              {/* Main form section */}
              <Box sx={{ mt: 2 }}>
                <Box sx={{ mb: 2 }}>
                  {/* Header Row: Title + Search */}
                  <Grid2 container spacing={2} sx={{ mb: 3 }} alignItems="center">
                    {/* Title */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        New Invoice
                      </Typography>
                    </Grid2>

                    {/* Item Search */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <ItemSearchField
                        ref={itemSearchFieldRef}
                        onSelectItem={(item) => handleSelectItemFromSearch(item, setFieldValue, values.items)}
                        clinicId={Number(ClinicId)}
                        deptId={selectedStoreId}
                        tariffId={selectedTariffId}
                        patientId={patientId ? Number(patientId) : undefined}
                        disabled={!!invoiceId} // Disable if editing existing invoice
                      />
                    </Grid2>
                  </Grid2>

                  {/* Second Row: Patient Source, Tariff, Package Selection, Referred Doctor */}
                  <Grid2 container spacing={2}>
                    
                    <Grid2 size={{ xs: 12, md: 4 }}>
                      <Autocomplete
                        disabled={!!invoiceId} // Disable if editing existing invoice
                        options={storeList}                  
                        value={storeList.find(opt => opt.id === selectedStoreId) || null}
                        onChange={(_, newValue) => {
                          setSelectedStoreId(newValue ? newValue.id : null);
                        }}
                        getOptionLabel={(option: Department) => option.name_label ?? ''}
                        noOptionsText={'No stores available'}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="store"
                            label={'Store'}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        fullWidth
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                      />
                    </Grid2>
                    
                    {/* Patient Source — hidden for new/temp patients */}
                    {!isNewPatient && (
                      <Grid2 size={{ xs: 12, md: 4 }}>
                        <PatientSourceSelect
                          options={patientSourceOptions}
                          value={values.patientSource}
                          onChange={(v) => {
                            setFieldValue('patientSource', v);
                            setFieldValue('tariff', '');
                            setFieldTouched('patientSource', true, false);
                          }}
                          error={errors.patientSource as string}
                          showErrors={showValidationErrors}
                          disabled={!!invoiceId} // Disable if editing existing invoice
                        />
                      </Grid2>
                    )}

                    {/* Tariff — hidden for new/temp patients */}
                    {!isNewPatient && (
                      <Grid2 size={{ xs: 12, md: 4 }}>
                        <TariffSelect
                          options={tariffOptions}
                          value={values.tariff}
                          onChange={(v) => {
                            setFieldValue('tariff', v);
                            setFieldTouched('tariff', true, false);
                          }}
                          error={errors.tariff as string}
                          showErrors={showValidationErrors}
                          placeholder={tariffPlaceholder}
                          disabled={!!invoiceId} // Disable if editing existing invoice
                        />
                      </Grid2>
                    )}

                    {/* Package Selection — hidden for new/temp patients */}
                    {!isNewPatient && (
                      <Grid2 size={{ xs: 12, md: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                          <Box sx={{ flex: 1 }}>
                            <PackageSelect
                              options={packageOptions}
                              value={values.packageId}
                              onChange={(v, pkg) => {
                                setFieldValue('packageId', v);
                                setFieldTouched('packageId', true, false);
                                setSelectedPackage(pkg);

                                // For donors: auto-assign the selected package to all items with matching package_details
                                if (isDonorOrSurrogate) {
                                  const pkgId = pkg?.id ?? null;
                                  const updated = values.items.map((item) => {
                                    if (!pkgId) {
                                      // Clear package from all items
                                      return {
                                        ...item,
                                        packageId: null,
                                        packageName: undefined,
                                        is_fromPackage: false,
                                        package_pharmacy_item_id: undefined,
                                        package_pending_qty: undefined,
                                        package_total_qty: undefined,
                                        uom_description: undefined,
                                      };
                                    }
                                    const matchingPkg = item.package_details?.find(
                                      (p) => p.package_id === pkgId
                                    );
                                    if (matchingPkg) {
                                      return {
                                        ...item,
                                        packageId: matchingPkg.package_id,
                                        packageName: matchingPkg.package_name,
                                        is_fromPackage: true,
                                        package_pharmacy_item_id: matchingPkg.package_pharmacy_item_id,
                                        package_pending_qty: matchingPkg.quantity_remaining,
                                        package_total_qty: matchingPkg.total_quantity,
                                        uom_description: matchingPkg.uom_description,
                                      };
                                    }
                                    return item;
                                  });
                                  setFieldValue('items', updated);

                                  // Track selected package for consumption modal
                                  if (pkgId) {
                                    const consumptionPkg = activePackagesData?.find((p) => p.id === pkgId);
                                    if (consumptionPkg) {
                                      setSelectedConsumptionPkg(consumptionPkg);
                                    }
                                  } else {
                                    setSelectedConsumptionPkg(null);
                                  }
                                }
                              }}
                              error={errors.packageId as string}
                              showErrors={showValidationErrors}
                              isLoading={isPackagesLoading && !activePackagesError}
                              disabled={!!invoiceId} // Disable if editing existing invoice
                            />
                          </Box>
                          {/* Icon to open package items modal - show when a package is selected for donors */}
                          {isDonorOrSurrogate && selectedConsumptionPkg && (
                            <Tooltip title="Open package items" arrow>
                              <IconButton
                                size="small"
                                onClick={() => setPackageItemModalOpen(true)}
                                sx={{
                                  color: '#5A8AEA',
                                  border: '1px solid #E4E7EC',
                                  borderRadius: 1.5,
                                  p: 0.5,
                                  mt: '8px',
                                  '&:hover': { borderColor: '#5A8AEA', bgcolor: '#F0F4FF' },
                                }}
                              >
                                <PlaylistAddRoundedIcon sx={{ fontSize: 24 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Grid2>
                    )}

                    {/* Referred Doctor - Read Only */}
                    <Grid2 size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Referred Doctor"
                        value={prescriptionsData?.data?.patient_info?.treating_doctor || 'N/A'}
                        InputProps={{ readOnly: true }}
                        sx={{ backgroundColor: 'white' }}
                        disabled={!!invoiceId} // Disable if editing existing invoice
                      />
                    </Grid2>

                    {/* Add Package Item - show when packages are available */}
                  </Grid2>
                </Box>

                {/* Item Details Section */}
                <Box sx={{ mt: 3, display:'grid' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Item Details ({values.items.length})
                  </Typography>
                  
                  <ItemsList
                    items={values.items}
                    onSelectChange={(idx: number) => {
                      const updated = [...values.items];
                      updated[idx].checked = !updated[idx].checked;
                      setFieldValue('items', updated);
                      
                      // Clear validation errors when unchecking an item
                      if (!updated[idx].checked) {
                        setFieldTouched(`items[${idx}].batchCode`, false);
                        setFieldTouched(`items[${idx}].billedQty`, false);
                        setFieldTouched(`items[${idx}].billedUOM`, false);
                      }
                    }}
                    onRemoveItem={(idx: number) => {
                      const updated = values.items.filter((_, i) => i !== idx);
                      setFieldValue('items', updated);
                    }}
                    onFieldChange={(idx: number, field: keyof FormPharmacyItem, value: string | number) => {
                      const updated = [...values.items];
                      updated[idx] = { ...updated[idx], [field]: value };
                      setFieldValue('items', updated);
                      
                      // Clear validation error when user enters a value
                      if (field === 'batchCode' || field === 'billedQty' || field === 'billedUOM') {
                        setFieldTouched(`items[${idx}].${field}`, false);
                      }
                    }}
                    onItemUpdate={(idx: number, updates: Partial<FormPharmacyItem>) => {
                      const updated = [...values.items];
                      updated[idx] = { ...updated[idx], ...updates };
                      setFieldValue('items', updated);
                      
                      // Clear validation errors for updated fields
                      if (updates.batchCode !== undefined) {
                        setFieldTouched(`items[${idx}].batchCode`, false);
                      }
                      if (updates.billedQty !== undefined) {
                        setFieldTouched(`items[${idx}].billedQty`, false);
                      }
                      if (updates.billedUOM !== undefined) {
                        setFieldTouched(`items[${idx}].billedUOM`, false);
                      }
                    }}
                    errors={Array.isArray(errors.items) ? errors.items as Array<Record<string, string>> : []}
                    touched={Array.isArray(touched.items) ? touched.items as Array<Record<string, boolean>> : []}
                    disabled={!!invoiceId} // Disable if editing existing invoice
                    invoiceStatus={prescriptionsData?.data?.invoice_status || ''}
                    packageId={values.packageId}
                    onPackageChange={(idx: number, pkgDetail: PackageDetail | null) => {
                      const updated = [...values.items];
                      if (pkgDetail) {
                        updated[idx] = {
                          ...updated[idx],
                          packageId: pkgDetail.package_id,
                          packageName: pkgDetail.package_name,
                          is_fromPackage: true,
                          package_pharmacy_item_id: pkgDetail.package_pharmacy_item_id ?? undefined,
                          package_pending_qty: pkgDetail.quantity_remaining ?? undefined,
                          package_total_qty: pkgDetail.total_quantity ?? undefined,
                          uom_description: pkgDetail.uom_description ?? undefined,
                        };
                      } else {
                        updated[idx] = {
                          ...updated[idx],
                          packageId: null,
                          packageName: undefined,
                          is_fromPackage: false,
                          package_pharmacy_item_id: undefined,
                          package_pending_qty: undefined,
                          package_total_qty: undefined,
                          uom_description: undefined,
                        };
                      }
                      setFieldValue('items', updated);
                    }}
                  />
                </Box>

                {/* Previous Prescribed Section */}
                {!isPrescriptionsLoading && (
                  <PreviousPrescribed
                    prescriptions={previousPrescriptions}
                    onMoveItems={(movedItems) => {
                      // Add moved items to the main items list
                      setFieldValue('items', [...values.items, ...movedItems]);
                    }}
                  />
                )}

                {/* Bottom section: concession, due date, notes, summary */}
                {(() => {
                  // Compute totals from selected items
                  const totals = values.items.reduce(
                    (acc, item) => {
                      if (!item.checked) return acc;
                      const mrpAmount = Number(item.totalMRPAmount) || 0;
                      const concession = Number(item.concessionAmount) || 0;
                      const tax = (item.taxes || []).reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
                      const net = Number(item.netAmount) || (mrpAmount - concession + tax);
                      return {
                        total: acc.total + mrpAmount,
                        concession: acc.concession + concession,
                        tax: acc.tax + tax,
                        net: acc.net + net,
                      };
                    },
                    { total: 0, concession: 0, tax: 0, net: 0 }
                  );

                  // Use package limit values calculated at form level (packagePharmacyTotal, totalPharmacyRemaining)

                  return (
                    <Grid2 container spacing={2} sx={{ mt: 3 }}>
                      <Grid2 size={{ xs: 12, md: 8 }}>
                        {/* Concession + Due Date row */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                          <Box sx={{ flex: 1 }}>
                            <Tooltip
                              title={!hasConcession ? 'No concession applied to any item' : ''}
                              arrow
                              placement="top"
                              disableHoverListener={hasConcession}
                            >
                              <span style={{ display: 'block', width: '100%' }}>
                                <TextField
                                  fullWidth
                                  select
                                  size="small"
                                  label="Concession Reason"
                                  value={values.concessionReason}
                                  onChange={(e) => {
                                    // Pass true as 3rd param to trigger validation after value is set
                                    setFieldValue('concessionReason', e.target.value, true);
                                  }}
                                  disabled={!hasConcession}
                                  error={!!(showValidationErrors && errors.concessionReason)}
                                  slotProps={{ inputLabel: { shrink: !!values.concessionReason || undefined } }}
                                >
                                  <MenuItem disabled value="">
                                    Select reason
                                  </MenuItem>
                                  {concessionReasonOptions.map((opt) => (
                                    <MenuItem key={opt.code} value={opt.code}>
                                      {opt.name}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </span>
                            </Tooltip>
                          </Box>

                          <Box sx={{ flex: 1 }}>
                            <TextField
                              fullWidth
                              type="date"
                              label={RequiredLabel('Due Date', true)}
                              value={values.dueDate}
                              onChange={(e) => setFieldValue('dueDate', e.target.value)}
                              size="small"
                              slotProps={{
                                inputLabel: { shrink: true },
                                htmlInput: { min: minDueDate, max: selectedDrugs?.length && !invoiceId ? dispensedDueDate : maxDueDate },
                              }}
                              error={!!(showValidationErrors && errors.dueDate && !values.dueDate)}
                            />
                          </Box>
                        </Box>

                        {/* Terms and conditions */}
                        <Box sx={{ mb: 2 }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Terms and Conditions"
                            value={values.termsAndConditions}
                            onChange={(e) => setFieldValue('termsAndConditions', e.target.value)}
                            placeholder="Ex. Payments can be made via cash, UPI, card, net banking, or bank transfer. Full payment is due at the time of service unless a credit facility is approved. Refunds, if applicable, will be processed within 7–10 working days."
                            size="small"
                            slotProps={{ inputLabel: { shrink: true } }}
                          />
                        </Box>

                        {/* Legal mentions */}
                        <Box sx={{ mb: 2 }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Legal Mentions"
                            value={values.legalMentions}
                            onChange={(e) => setFieldValue('legalMentions', e.target.value)}
                            placeholder="Ex. Medical services may be tax-exempt under applicable laws. Patient details are confidential and protected under privacy regulations. All billing disputes must be reported within 7 days of invoice generation."
                            size="small"
                            slotProps={{ inputLabel: { shrink: true } }}
                          />
                        </Box>

                        {/* Remarks & comments */}
                        <Grid2 container spacing={2}>
                          <Grid2 size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              label="Remark"
                              placeholder="Type Here.."
                              value={values.remark}
                              onChange={(e) => setFieldValue('remark', e.target.value)}
                              size="small"
                              slotProps={{ inputLabel: { shrink: !!values.remark || undefined } }}
                            />
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              label="Comments"
                              placeholder="Type Here..."
                              value={values.comments}
                              onChange={(e) => setFieldValue('comments', e.target.value)}
                              size="small"
                              slotProps={{ inputLabel: { shrink: !!values.comments || undefined } }}
                            />
                          </Grid2>
                        </Grid2>
                      </Grid2>

                      {/* Summary card */}
                      <Grid2 size={{ xs: 12, md: 4 }}>
                        <SummaryCard
                          total={totals.total}
                          concession={totals.concession}
                          tax={totals.tax}
                          net={totals.net}
                          consumeFromPackage={consumeFromPackageAmount}
                          packageServicesTotal={packagePharmacyTotal}
                          packageServiceLimit={totalPharmacyRemaining}
                          advanceBalanceAmount={totalAdvanceBalance}
                        />
                      </Grid2>
                    </Grid2>
                  );
                })()}
              </Box>

              {/* Footer actions */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    navigate(`/admin/billing/invoice/patient-invoice/${patientId}`);                    
                    // navigate(`/admin/billing/pharmacy/patient-pharmacy/${patientId}`);
                  }}
                  sx={outlinedGrayButtonSx}
                >
                  Back to List
                </Button>

                {/* Collect Package Advance Button - only show if a package is selected */}
                {values.packageId && (
                  <Button
                    variant="outlined"
                    onClick={handleOpenCollectPackageAdvance}
                    sx={outlinedGrayButtonSx}
                  >
                    Collect Package Advance
                  </Button>
                )}

                {/* Collect Payment Button - disabled when package limit exceeded or no items */}
                <Tooltip 
                  title={
                    values.items.length === 0 
                      ? 'Add items to proceed with payment' 
                      : isPackageLimitExceeded 
                        ? 'Package pharmacy limit exceeded. Remove some items from package or reduce quantities.' 
                        : ''
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant={isDonorOrSurrogate ? 'outlined' : 'contained'}
                      color={isDonorOrSurrogate ? undefined : 'primary'}
                      disabled={isSubmitting || createPharmacyInvoiceMutation.isPending || isPackageLimitExceeded || values.items.length === 0}
                      sx={{
                        fontWeight: 700,
                        ...(isDonorOrSurrogate && {
                          borderColor: '#bdbdbd',
                          color: '#616161',
                          '&:hover': {
                            borderColor: '#9e9e9e',
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }),
                      }}
                      onClick={async () => {
                        // eslint-disable-next-line no-console
                        console.log('Collect Payment clicked - packageId:', values.packageId);
                        setShowValidationErrors(true);
                        const validationErrors = await validateForm();
                        const isValid = Object.keys(validationErrors).length === 0;
                    
                        if (!isValid) {
                          // Mark only checked items fields as touched to show validation errors
                          setTouched({
                            patientSource: true,
                            tariff: true,
                            items: values.items.map((item) => 
                              item.checked ? {
                                batchCode: true,
                                billedQty: true,
                                billedUOM: true,
                                prescribedQty: true,
                                prescribedUOM: true,
                              } : {}
                            ),
                          });
                          // eslint-disable-next-line no-console
                          console.error('Form validation failed:', validationErrors);
                          return;
                        }

                        // Additional validation checks
                        const selectedItems = values.items.filter((item) => item.checked);
                        if (selectedItems.length === 0) {
                          // eslint-disable-next-line no-console
                          console.error('No items selected for billing');
                          alert('Please select at least one item to proceed with payment');
                          return;
                        }

                        // Block submit if any selected item has qty that exceeds prescribed remaining or available stock
                        // Skip this check for dispensed invoices — fields are read-only and already saved 
                        const isDispensedInvoice = prescriptionsData?.data?.invoice_status === 'dispensed';
                        if (!isDispensedInvoice && hasAnyQtyError(selectedItems)) {
                          addAlert('Some items have quantity errors. Please review the highlighted quantities before proceeding.', 'error');
                          return;
                        }

                        // Check if all selected items have batch, qty, and UOM
                        // Skip for dispensed invoices where these fields are pre-populated and read-only
                        const invalidItems = isDispensedInvoice ? [] : selectedItems.filter(
                          (item) => !item.batchCode || !item.billedQty || !item.billedUOM
                        );
                    
                        if (invalidItems.length > 0) {
                          // Mark invalid items as touched
                          const touchedItems = values.items.map((item) => {
                            const isInvalid = selectedItems.includes(item) && (!item.batchCode || !item.billedQty || !item.billedUOM);
                            return isInvalid ? {
                              batchCode: true,
                              billedQty: true,
                              billedUOM: true,
                            } : {};
                          });
                          setTouched({
                            ...touched,
                            items: touchedItems,
                          });
                          // eslint-disable-next-line no-console
                          console.error('Some items are missing required fields:', invalidItems);
                          alert('Please select batch code, quantity, and UOM for all selected items');
                          return;
                        }

                        // If all items are from package and fully covered, save directly
                        if (isFullyCoveredByPackage) {
                          const packagesForValidation = (() => {
                            const pkgMap = new Map<number, number>();
                            const formPkgId = values.packageId ? parseInt(values.packageId) : 0;
                            selectedItems.forEach((item) => {
                              const pkgId = item.packageId ? parseInt(String(item.packageId)) : formPkgId;
                              if (pkgId) {
                                pkgMap.set(pkgId, (pkgMap.get(pkgId) || 0) + (Number(item.netAmount) || 0));
                              }
                            });
                            return Array.from(pkgMap.entries()).map(([package_id, amount]) => ({ package_id, amount }));
                          })();
                          try {
                            const validationResult = await validatePackageAdvanceMutation.mutateAsync(
                              buildValidatePackageAdvanceParams(packagesForValidation)
                            );
                            if (validationResult.overall_valid === true) {
                              setFormValuesForPayment(values);
                              setPackageFullPayConfirmOpen(true);
                            } else {
                              if (validationResult.packages) {
                                setValidationResultPackages(validationResult.packages.map((pkg) => ({
                                  package_id: pkg.package_id,
                                  required_amount: pkg.required_amount || 0,
                                  balance_amount: pkg.balance_amount || 0,
                                })));
                              }
                              setInsufficientAdvanceOpen(true);
                            }
                          } catch (err) {
                            // eslint-disable-next-line no-console
                            console.error('Package advance validation failed:', err);
                          }
                          return;
                        }
                    
                        // Calculate totals for payment modal
                        const totals = selectedItems.reduce(
                          (acc, item) => ({
                            total: acc.total + item.totalMRPAmount,
                            concession: acc.concession + item.concessionAmount,
                            tax: acc.tax + (item.taxes || []).reduce((sum, t) => sum + (Number(t.amount) || 0), 0),
                            net: acc.net + item.netAmount,
                          }),
                          { total: 0, concession: 0, tax: 0, net: 0 }
                        );
                    
                        // Round net amount to avoid floating point precision issues
                        const roundedNetAmount = Math.round((totals.net + Number.EPSILON) * 100) / 100;
                    
                        // Set payment details for modal
                        setPaymentDetailsForModal({
                          date: formatDateYMD(new Date()),
                          patientName: currentPatient?.first_name + ' ' + currentPatient?.last_name || 'Patient',
                          patientId: patientMRN || patientId || '',
                          patientNumericId: patientId,
                          currency: currency,
                          netAmount: roundedNetAmount,
                        });
                    
                        // Build array of packages from selected items
                        // Group items by package_id if they have one, else use form's packageId or 0
                        const packagesForValidation: Array<{ package_id: number; amount: number }> = [];
                        const packageMap = new Map<number, number>();
                    
                        const formPackageId = values.packageId ? parseInt(values.packageId) : 0;
                    
                        selectedItems.forEach((item) => {
                          // Use item's packageId if available, otherwise use form's packageId, otherwise 0
                          const pkgId = item.packageId ? parseInt(String(item.packageId)) : formPackageId;
                          const existing = packageMap.get(pkgId) || 0;
                          packageMap.set(pkgId, existing + item.netAmount);
                        });
                    
                        // If no items grouped by package but form has packageId, use that
                        if (packageMap.size === 0 && formPackageId > 0) {
                          packageMap.set(formPackageId, roundedNetAmount);
                        }
                    
                        // Convert map to array for API
                        Array.from(packageMap.entries()).forEach(([pkgId, amount]) => {
                          packagesForValidation.push({
                            package_id: pkgId,
                            amount,
                          });
                        });
                    
                        // eslint-disable-next-line no-console
                        console.log('Packages for validation:', packagesForValidation, 'Form packageId:', formPackageId);
                    
                        // Always validate packages even if empty or contains 0
                        // This allows wallet payment for general pharmacy items
                        try {
                          const validationResult = await validatePackageAdvanceMutation.mutateAsync(
                            buildValidatePackageAdvanceParams(packagesForValidation)
                          );
                      
                          // DEBUG: Log the validation result
                          // eslint-disable-next-line no-console
                          console.log('Pharmacy - Validation Result:', validationResult);
                      
                          /**
                       * VALIDATION RESULT HANDLING
                       * -------------------------------------------------------------
                       * overall_valid === true:
                       *   - Package has sufficient advance to cover the consumed amount
                       *   - Package consumption is auto-debited separately
                       *   - Open payment modal for user to pay remaining amount normally
                       * 
                       * overall_valid === false:
                       *   - Package has INSUFFICIENT advance
                       *   - Show InsufficientAdvancePopup
                       *   - User must collect more advance before proceeding
                       */
                          if (validationResult.overall_valid === false) {
                            // Store validation result packages with required_amount for auto-populating advance modal
                            if (validationResult.packages) {
                              const storedPackages = validationResult.packages.map((pkg) => ({
                                package_id: pkg.package_id,
                                required_amount: pkg.required_amount || 0,
                                balance_amount: pkg.balance_amount || 0,
                              }));
                              if (process.env.NODE_ENV === 'development') {
                                // eslint-disable-next-line no-console
                                console.log('Storing validation result packages:', storedPackages);
                              }
                              setValidationResultPackages(storedPackages);
                            }
                        
                            // Show insufficient advance popup
                            setInsufficientAdvanceOpen(true);
                            return; // Exit early - don't open payment modal
                          }
                      
                          // Map package validation response to wallet advances
                          const mappedAdvances: WalletAdvance[] = [];
                          if (validationResult.packages && validationResult.packages.length > 0) {
                            validationResult.packages.forEach((pkg, pkgIndex) => {
                              // eslint-disable-next-line no-console
                              console.log(`Pharmacy - Package ${pkgIndex}:`, pkg);
                              if (pkg.credit_transactions && pkg.credit_transactions.length > 0) {
                                // eslint-disable-next-line no-console
                                console.log(`Pharmacy - Credit Transactions for package ${pkgIndex}:`, pkg.credit_transactions);
                                pkg.credit_transactions.forEach((txn) => {
                                  mappedAdvances.push({
                                    creditTransactionId: txn.credit_transaction_id,
                                    date: formatDateDMY(txn.transaction_date),
                                    advanceAgainst: pkg.package_id ? `Package ${pkg.package_id}` : 'General',
                                    packageName: pkg.package_id ? `Package ${pkg.package_id}` : 'General',
                                    packageId: pkg.package_id,
                                    total: txn.total_amount,
                                    used: txn.used_amount,
                                    balance: txn.balance_amount,
                                    isSelected: true,
                                    consumeAmount: Math.min(roundedNetAmount, txn.balance_amount),
                                  } as WalletAdvance);
                                });
                              }
                            });
                          }
                          // eslint-disable-next-line no-console
                          console.log('Pharmacy - Mapped Advances:', mappedAdvances);
                          setWalletAdvances(mappedAdvances);
                      
                          // Calculate total wallet balance
                          const uniqueBalanceMap = new Map<number, number>();
                          mappedAdvances.forEach((adv) => {
                            if (adv.creditTransactionId && !uniqueBalanceMap.has(adv.creditTransactionId)) {
                              uniqueBalanceMap.set(adv.creditTransactionId, adv.balance);
                            }
                          });
                          const totalBalance = Array.from(uniqueBalanceMap.values()).reduce((a, b) => a + b, 0);
                          // eslint-disable-next-line no-console
                          console.log('Pharmacy - Total Balance:', totalBalance);
                          setWalletBalance(totalBalance);
                      
                          // Auto-select wallet payment if there's available balance
                          const totalWalletAmount = mappedAdvances
                            .filter((a) => a.isSelected)
                            .reduce((sum, a) => sum + a.consumeAmount, 0);
                      
                          // eslint-disable-next-line no-console
                          console.log('Pharmacy - Total Wallet Amount (to consume):', totalWalletAmount);
                      
                          if (totalWalletAmount > 0) {
                            // Group consumptions by credit_transaction_id
                            const consumptionsMap = new Map<number, number>();
                            mappedAdvances.forEach((adv) => {
                              if (adv.isSelected && adv.creditTransactionId && adv.consumeAmount > 0) {
                                const existing = consumptionsMap.get(adv.creditTransactionId) || 0;
                                consumptionsMap.set(adv.creditTransactionId, existing + adv.consumeAmount);
                              }
                            });
                        
                            const creditConsumptions = Array.from(consumptionsMap.entries()).map(
                              ([credit_transaction_id, amount]) => ({
                                credit_transaction_id,
                                consume_amount: String(amount),
                              })
                            );
                        
                            // Find Wallet payment mode for wallet payments
                            const walletMode = paymentModes.find((m) => m.code?.toLowerCase() === 'wallet' || m.name?.toLowerCase() === 'wallet');
                        
                            // Check if ALL selected advances are from packages (not 'General')
                            const selectedAdvances = mappedAdvances.filter((a) => a.isSelected && a.consumeAmount > 0);
                            const isAllFromPackage = selectedAdvances.length > 0 && 
                              selectedAdvances.every((a) => a.packageId || (a.advanceAgainst && a.advanceAgainst !== 'General'));
                        
                            const walletHistoryItem: PaymentHistoryItem = {
                              id: `wallet-${Date.now()}`,
                              method: isAllFromPackage ? 'Package Advance' : 'Wallet',
                              amount: totalWalletAmount,
                              payment_mode_id: walletMode?.id || 0,
                              date: formatDateYMD(new Date()),
                              credit_transaction_consumptions: creditConsumptions,
                            };
                            setInitialPaymentHistory([walletHistoryItem]);
                          } else {
                            setInitialPaymentHistory([]);
                          }
                        } catch  {
                          setWalletAdvances([]);
                          setWalletBalance(0);
                          setInitialPaymentHistory([]);
                        }    
                    
                        // Calculate consume from package amount for the modal
                        const packageItemsTotal = selectedItems
                          .filter((item) => item.packageId || values.packageId)
                          .reduce((sum, item) => sum + (Number(item.netAmount) || 0), 0);
                        const pharmacyRemaining = activePackagesData?.reduce(
                          (sum, pkg) => sum + (pkg.pharmacy_remaining_amount ?? 0), 0
                        ) ?? 0;
                        const consumeFromPkg = values.packageId ? Math.min(packageItemsTotal, pharmacyRemaining) : 0;
                        setConsumeFromPackageForModal(consumeFromPkg);

                        // Open collect payment modal
                        setCollectPaymentOpen(true);
                      }}
                    >
                      {createPharmacyInvoiceMutation.isPending
                        ? 'Processing...'
                        : isFullyCoveredByPackage
                          ? 'Save'
                          : isDonorOrSurrogate
                            ? 'Continue with Donor'
                            : 'Collect Payment'}
                    </Button>
                  </span>
                </Tooltip>

                {/* Continue with Recipient button - only for donor/surrogate patients */}
                {/* Hidden when all items are from a package — the package already identifies the recipient */}
                {isDonorOrSurrogate && !allItemsFromPackage && (
                  <Button
                    variant="contained"
                    disabled={
                      isSubmitting ||
                      values.items.length === 0 ||
                      isPackageLimitExceeded
                    }
                    sx={{
                      fontWeight: 700,
                      backgroundColor: '#424242',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: '#333',
                      },
                    }}
                    onClick={async () => {
                      setShowValidationErrors(true);
                      const validationErrors = await validateForm();
                      const isValid = Object.keys(validationErrors).length === 0;
                      if (isValid) {
                        // Additional checks for pharmacy items
                        const selectedItems = values.items.filter((item) => item.checked);
                        if (selectedItems.length === 0) {
                          addAlert('Please select at least one item to proceed', 'error');
                          return;
                        }
                        const invalidItems = selectedItems.filter(
                          (item) => !item.batchCode || !item.billedQty || !item.billedUOM
                        );
                        if (invalidItems.length > 0) {
                          addAlert('Please select batch code, quantity, and UOM for all selected items', 'error');
                          return;
                        }

                        setPendingFormValues(values);
                        setRecipientsLoading(true);
                        try {
                          const donorId = patientId ? Number(patientId) : currentPatient?.id;
                          if (donorId) {
                            const data = await fetchLinkedRecipients(donorId);
                            const recipientList = data?.recipients ?? [];
                            setRecipients(recipientList);
                            if (recipientList.length === 1) {
                              setSelectedRecipientId(recipientList[0].id);
                            } else {
                              setSelectedRecipientId(null);
                            }
                          }
                        } catch (err) {
                          // eslint-disable-next-line no-console
                          console.error('Failed to fetch recipients:', err);
                          setRecipients([]);
                        } finally {
                          setRecipientsLoading(false);
                        }
                        setRecipientModalOpen(true);
                      }
                    }}
                  >
                    Continue with Recipient
                  </Button>
                )}
              </Box>
            </form>
          );
        }}
      </Formik>

      {/* Batch Selection Modal */}
      {selectedItemForBatch && (
        <BatchSelectionModal
          open={batchModalOpen}
          onClose={handleBatchModalClose}
          onConfirm={handleBatchSelect}
          itemName={selectedItemForBatch.item_name}
          itemCode={selectedItemForBatch.item_code}
          batches={selectedItemForBatch.batches}
          existingItems={formikRef.current?.values.items.map(item => ({
            itemCode: item.itemCode,
            batchCode: item.batchCode,
          })) || []}
        />
      )}

      {/* Collect Payment Modal */}
      {paymentDetailsForModal && (
        <CollectPaymentModal
          open={collectPaymentOpen}
          onClose={() => {
            setCollectPaymentOpen(false);
            setConsumeFromPackageForModal(0);
          }}
          onSave={handlePaymentSave}
          paymentDetails={paymentDetailsForModal}
          existingPaymentHistory={initialPaymentHistory}
          walletBalance={walletBalance}
          walletAdvances={walletAdvances}
          paymentModes={paymentModes}
          showWallet={true}  // Enable wallet for non-package pharmacy items
          maxCreditAmount={0}
          consumeFromPackage={consumeFromPackageForModal}
          isSubmitting={createPharmacyInvoiceMutation.isPending}
        />
      )}

      {/**
       * PACKAGE ADVANCE 3-STEP FLOW
       * Step 1: User fills advance form (package, amount, date, remarks)
       * Step 2: User selects payment method
       * Step 3: Save happens in handleSavePackageAdvance
       */}
      
      {/* Step 1: Package Advance Form - select package & enter amount */}
      {packageOptionsForAdvance.length > 0 && (
        <CollectPackageAdvanceModal
          open={collectPackageAdvanceOpen}
          onClose={() => {
            setCollectPackageAdvanceOpen(false);
            // Don't clear packageOptionsForAdvance here - user might reopen
          }}
          onCollectPayment={handlePackageAdvanceCollectPayment}  // Transitions to Step 2
          packages={packageOptionsForAdvance}
        />
      )}

      {/* Step 2: Payment Methods - select how to pay the advance  */}
      {packageAdvancePaymentDetails && (
        <CollectPaymentModal
          open={collectPackagePaymentOpen}
          onClose={() => {
            setCollectPackagePaymentOpen(false);
            // Clear payment details but preserve form data for potential retry
            setPackageAdvancePaymentDetails(null);
          }}
          paymentType="ADVANCE"
          onSave={handleSavePackageAdvance}  // Step 3: Final save
          paymentDetails={packageAdvancePaymentDetails}
          paymentModes={paymentModes}
          hidePaymentModeToggle={true}
          showWallet={false}  // Wallet disabled for collecting advance itself
          existingPaymentHistory={[]}
          walletAdvances={[]}
          isSubmitting={isAdvanceSubmitting}
        />
      )}

      {/**
       * INSUFFICIENT ADVANCE POPUP
       * Shown when package advance validation fails (overall_valid === false).
       * On confirm, opens the 3-step package advance collection flow.
       */}
      <InsufficientAdvancePopup
        open={insufficientAdvanceOpen}
        onClose={() => setInsufficientAdvanceOpen(false)}
        onCollectAdvance={handleOpenCollectPackageAdvanceExternal}
      />

      {/* Package Full Pay Confirmation - when all items from package with sufficient advance */}
      <ConfirmationModal
        open={packageFullPayConfirmOpen}
        handleClose={() => setPackageFullPayConfirmOpen(false)}
        onConfirm={() => { handlePackageFullPaySave(); }}
        type="save"
        title="Save Invoice"
        contentMessage={
          <>
            All items will be consumed from package advance.
            <br />
            Do you want to save this invoice?
          </>
        }
      />

      {/* Package Items Modal - Add items from package consumption details */}
      {selectedConsumptionPkg && (
        <PharmacyPackageItemsModal
          open={packageItemModalOpen}
          onClose={() => setPackageItemModalOpen(false)}
          onSave={(selectedItems: PharmacyPackageConsumptionItem[]) => {
            if (!formikRef.current) return;
            const currentItems = formikRef.current.values.items;
            const pkgId = selectedConsumptionPkg.id;
            const pkgName = selectedConsumptionPkg.package_name;

            const mapped: FormPharmacyItem[] = selectedItems.map((item) => {
              // Pick the first available (non-held, in-stock) batch
              const bestBatch = item.batches.find((b) => b.available_stock > 0 && !b.is_hold);
              const baseMRP = bestBatch?.base_mrp || Number(item.mrp) || 0;
              const billedQty = 1;

              // Build taxes from the batch or item-level tax percentages
              const baseTaxes: Array<{ name: string; percent: number; amount: number }> = [];
              const sgstPct = bestBatch?.sgst_percent ?? item.SGST ?? 0;
              const cgstPct = bestBatch?.cgst_percent ?? item.CGST ?? 0;
              if (sgstPct > 0) baseTaxes.push({ name: 'SGST', percent: sgstPct, amount: 0 });
              if (cgstPct > 0) baseTaxes.push({ name: 'CGST', percent: cgstPct, amount: 0 });
              const taxCalc = calculateFromInclusiveMRP(baseMRP, billedQty, baseTaxes);

              // Map all batches to available batch options
              const availableBatches = item.batches.map((b) => {
                const bTaxes: Array<{ name: string; percent: number; amount: number }> = [];
                const bSgst = b.sgst_percent ?? item.SGST ?? 0;
                const bCgst = b.cgst_percent ?? item.CGST ?? 0;
                if (bSgst > 0) bTaxes.push({ name: 'SGST', percent: bSgst, amount: 0 });
                if (bCgst > 0) bTaxes.push({ name: 'CGST', percent: bCgst, amount: 0 });
                return {
                  batchId: b.id,
                  batchNumber: b.batch_number,
                  expiryDate: formatDateDMY(b.expiry_date),
                  isExpired: new Date(b.expiry_date) < new Date(),
                  baseMrp: b.base_mrp,
                  baseCostPrice: b.base_cost,
                  taxes: bTaxes,
                  availableStock: b.available_stock,
                  availableUomId: b.available_uom_id,
                  availableUom: b.available_uom_name ?? item.base_uom_name,
                  storeId: b.store_id,
                };
              });

              return {
                prescriptionId: 0,
                itemId: item.item_id,
                visitDate: formatDateDMY(new Date()),
                itemCode: item.item_code,
                itemName: item.item_name,
                batchCode: bestBatch?.batch_number || '',
                batchId: bestBatch?.id,
                expiryDate: bestBatch ? formatDateDMY(bestBatch.expiry_date) : '',
                manufacturer: item.item_group_name || '',
                availableBatches,
                prescribedStrength: '',
                prescribedQty: item.quantity_remaining,
                prescribedUOM: item.base_uom_name,
                billedStrength: '',
                billedQty: billedQty,
                billedUOM: item.base_uom_name,
                rack: item.rack_name || '',
                shelf: item.shelf_name || '',
                container: item.bin_name || '',
                checked: true,
                baseMRP: baseMRP,
                baseCostPrice: bestBatch?.base_cost || Number(item.cost_price) || 0,
                totalMRPAmount: baseMRP * billedQty,
                sellingPricePerUnit: taxCalc.sellingPricePerUnit,
                totalSellingPrice: taxCalc.totalSellingPrice,
                concessionAmount: 0,
                concessionPercent: item.discount_percent || 0,
                taxes: taxCalc.taxesWithAmounts,
                netAmount: taxCalc.netAmount,
                pregnancyClass: '',
                provider: '',
                availableStock: bestBatch?.available_stock,
                availableUomId: bestBatch?.available_uom_id,
                availableUom: bestBatch?.available_uom_name ?? item.base_uom_name,
                storeId: bestBatch?.store_id,
                packageId: pkgId,
                packageName: pkgName,
                is_fromPackage: true,
                package_pharmacy_item_id: item.package_pharmacy_item_id,
                package_total_qty: item.total_quantity,
                package_pending_qty: item.quantity_remaining,
                uomConversions: item.uom_conversions?.map((u) => ({
                  id: u.id,
                  item_id: u.item_id,
                  item_name: u.item_name,
                  uom_id: u.uom_id,
                  uom_code: u.uom_code,
                  code: u.code,
                  uom_static_field: u.uom_static_field || u.uom_code,
                  conversion_factor: u.conversion_factor,
                  converted_mrp: u.converted_mrp,
                  converted_cost_price: u.converted_cost_price,
                })),
                tariffId: null,
                tariffName: undefined,
              };
            });

            formikRef.current.setFieldValue('items', [...currentItems, ...mapped]);
            setPackageItemModalOpen(false);
          }}
          packageId={selectedConsumptionPkg.id}
          packageName={selectedConsumptionPkg.package_name}
          deptId={selectedStoreId}
        />
      )}

      {/* Recipient Selection Modal - shown when donor/surrogate clicks "Continue with Recipient" */}
      <RecipientSelectionModal
        open={recipientModalOpen}
        onClose={() => setRecipientModalOpen(false)}
        recipients={recipients}
        selectedRecipientId={selectedRecipientId}
        onSelectRecipient={setSelectedRecipientId}
        loading={recipientsLoading}
        onProceed={handleRecipientProceed}
      />
    </>
  );
};

export default NewPharmacyForm;
